// ============================================
// ANALYTICA V9.0 - FRONTEND SIMPLIFICADO
// Pipeline de Duplicidad, Paso3, Paso4, Paso5
// ============================================

import React, { useState, useEffect, Suspense } from 'react';
import { Toaster, toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine, Cell } from 'recharts';
import { ActionOverlayProvider } from './components/ActionOverlay';
import AppLauncher from './components/AppLauncher';
import { ScrapePendientesPanel } from './components/ScrapePendientesPanel';

// ============================================
// v9.6.0 — CARGA DIFERIDA DE MÓDULOS (code splitting)
// Antes los módulos se importaban de entrada: todo el sistema (y librerías
// como xlsx) se descargaba y procesaba antes de mostrar siquiera el login.
// Ahora cada módulo es un chunk aparte que se descarga al abrirlo.
// lazyConRecarga: si tras un despliegue el chunk viejo ya no existe en el
// servidor (ChunkLoadError), recarga la página UNA vez para tomar la versión
// nueva en lugar de dejar la pantalla en blanco.
// ============================================
const lazyConRecarga = (importar) => React.lazy(() =>
  importar()
    .then((mod) => {
      try { sessionStorage.removeItem('chunk_reload'); } catch (_) { /* noop */ }
      return mod;
    })
    .catch((err) => {
      let yaRecargo = false;
      try { yaRecargo = sessionStorage.getItem('chunk_reload') === '1'; } catch (_) { /* noop */ }
      if (!yaRecargo) {
        try { sessionStorage.setItem('chunk_reload', '1'); } catch (_) { /* noop */ }
        window.location.reload();
        return new Promise(() => {});
      }
      throw err;
    })
);

const LabPromoModule = lazyConRecarga(() => import('./components/LabPromoModule'));
const ComfarmalsaApp = lazyConRecarga(() => import('./components/ComfarmalsaApp'));
const InformeInvestigacionManager = lazyConRecarga(() => import('./components/InformeInvestigacionManager'));
const CompetitividadModule = lazyConRecarga(() => import('./components/CompetitividadModule'));
const CapturaDashboard = lazyConRecarga(() => import('./components/CapturaDashboard'));
const AdminDBModule = lazyConRecarga(() => import('./components/AdminDBModule'));
const RecomendacionesIAModule = lazyConRecarga(() => import('./components/RecomendacionesIAModule'));
const DistribucionModule = lazyConRecarga(() => import('./components/DistribucionModule'));
const SimulacionVentaModule = lazyConRecarga(() => import('./components/SimulacionVentaModule'));
const GestorUsuariosModule = lazyConRecarga(() => import('./components/GestorUsuariosModule'));
const UploadsAdminModule = lazyConRecarga(() => import('./components/UploadsAdminModule'));
const PMTPromocionesModule = lazyConRecarga(() => import('./components/PMTPromocionesModule'));
const SimuladorDispersionModule = lazyConRecarga(() => import('./components/SimuladorDispersionModule'));
const AnalisisMargenesModule = lazyConRecarga(() => import('./components/AnalisisMargenesModule'));
const GuiaSimuladaModule = lazyConRecarga(() => import('./components/GuiaSimuladaModule'));
const PromoPoliticaModule = lazyConRecarga(() => import('./components/PromoPoliticaModule'));
const RevionicsModule = lazyConRecarga(() => import('./components/RevionicsModule'));
const GuiaCompetenciaModule = lazyConRecarga(() => import('./components/GuiaCompetenciaModule'));

const ModuloCargando = () => (
  <div className="flex items-center justify-center py-20 text-slate-500" data-testid="modulo-cargando">
    <div className="w-6 h-6 mr-3 border-2 border-slate-300 border-t-indigo-600 rounded-full animate-spin" />
    <span className="text-sm">Cargando módulo...</span>
  </div>
);

const APP_VERSION = "9.3.1";

// ============================================
// CONFIGURACIÓN DE API - PRODUCCIÓN Y PREVIEW
// ============================================
// En producción: usa el mismo origen (window.location.origin)
// En desarrollo: usa REACT_APP_BACKEND_URL si está definida
const getApiUrl = () => {
  // Si estamos en el navegador
  if (typeof window !== 'undefined') {
    const currentHost = window.location.hostname;
    
    // Si es localhost, usar variable de entorno o localhost
    if (currentHost === 'localhost' || currentHost === '127.0.0.1') {
      return process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
    }
    
    // En cualquier otro caso (producción, preview, etc.), usar el mismo origen
    // Esto asegura que las llamadas API vayan al mismo dominio
    return window.location.origin;
  }
  
  // Fallback para SSR o casos edge
  return process.env.REACT_APP_BACKEND_URL || '';
};

const API = getApiUrl();

// Log para debugging (solo en desarrollo)
if (process.env.NODE_ENV === 'development') {
  console.log('[Analytica] API URL:', API);
}

// ============================================
// FETCH CON REINTENTOS Y MANEJO DE ERRORES
// ============================================

/**
 * Códigos de error de gateway que son recuperables con reintentos
 */
const RETRYABLE_CODES = [502, 503, 504, 520, 521, 522, 524];

/**
 * Fetch con manejo robusto de errores y reintentos automáticos
 * @param {string} url - URL del endpoint
 * @param {object} options - Opciones de fetch (headers, method, body, etc.)
 * @param {object} config - Configuración adicional
 * @param {number} config.maxRetries - Número máximo de reintentos (default: 2)
 * @param {number} config.baseDelay - Delay base en ms para backoff (default: 1000)
 * @param {number} config.timeout - Timeout en ms (default: 120000)
 * @returns {Promise<{ok: boolean, status: number, data: object|null, error: string|null}>}
 */
async function fetchJsonWithGuard(url, options = {}, config = {}) {
  const {
    maxRetries = 2,
    baseDelay = 1000,
    timeout = 120000
  } = config;
  
  // Asegurar headers por defecto
  const headers = {
    'Accept': 'application/json',
    ...options.headers
  };
  
  let lastError = null;
  let lastStatus = 0;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    
    try {
      const response = await fetch(url, {
        ...options,
        headers,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      lastStatus = response.status;
      
      // Verificar Content-Type antes de parsear
      const contentType = response.headers.get('content-type') || '';
      
      // Si es error de gateway y quedan reintentos, reintentar
      if (RETRYABLE_CODES.includes(response.status) && attempt < maxRetries) {
        const delay = baseDelay * Math.pow(2, attempt) + Math.random() * 500; // Exponential backoff + jitter
        console.warn(`[fetchJsonWithGuard] Retry ${attempt + 1}/${maxRetries} after ${response.status}, waiting ${Math.round(delay)}ms`);
        await new Promise(r => setTimeout(r, delay));
        continue;
      }
      
      // Si la respuesta no es JSON, manejar el error
      if (!contentType.includes('application/json')) {
        const text = await response.text().catch(() => '');
        console.error(`[fetchJsonWithGuard] Non-JSON response (${response.status}):`, text.substring(0, 200));
        
        return {
          ok: false,
          status: response.status,
          data: null,
          error: getErrorMessage(response.status, text)
        };
      }
      
      // Parsear JSON
      const data = await response.json();
      
      return {
        ok: response.ok,
        status: response.status,
        data,
        error: response.ok ? null : (data.detail || data.message || 'Error desconocido')
      };
      
    } catch (err) {
      clearTimeout(timeoutId);
      lastError = err;
      
      // Si es timeout o error de red y quedan reintentos, reintentar
      if ((err.name === 'AbortError' || err.name === 'TypeError') && attempt < maxRetries) {
        const delay = baseDelay * Math.pow(2, attempt) + Math.random() * 500;
        console.warn(`[fetchJsonWithGuard] Retry ${attempt + 1}/${maxRetries} after ${err.name}, waiting ${Math.round(delay)}ms`);
        await new Promise(r => setTimeout(r, delay));
        continue;
      }
    }
  }
  
  // Si llegamos aquí, todos los reintentos fallaron
  console.error('[fetchJsonWithGuard] All retries exhausted:', lastError);
  
  return {
    ok: false,
    status: lastStatus || 0,
    data: null,
    error: lastError?.name === 'AbortError' 
      ? 'La operación tardó demasiado. Intente de nuevo.'
      : 'Error de conexión. Verifique su conexión a internet.'
  };
}

/**
 * Obtener mensaje de error amigable según código de estado
 */
function getErrorMessage(status, responseText = '') {
  const messages = {
    400: 'Solicitud inválida',
    401: 'Sesión expirada. Por favor inicie sesión nuevamente.',
    403: 'No tiene permisos para esta operación',
    404: 'Recurso no encontrado',
    422: 'Datos inválidos',
    429: 'Demasiadas solicitudes. Espere un momento.',
    500: 'Error interno del servidor',
    502: 'El servidor está reiniciando. Intente en unos segundos.',
    503: 'Servicio no disponible. Intente en unos segundos.',
    504: 'Tiempo de espera agotado. Intente de nuevo.',
    520: 'El servidor está temporalmente ocupado. Intente de nuevo.',
    521: 'El servidor no está disponible. Intente más tarde.',
    522: 'Tiempo de conexión agotado. Intente de nuevo.',
    524: 'La operación tardó demasiado. Intente de nuevo.'
  };
  
  return messages[status] || `Error del servidor (${status})`;
}

// ============================================
// COMPONENTE PRINCIPAL
// ============================================
function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('home');
  const [maestrosCargando, setMaestrosCargando] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [launcherOpen, setLauncherOpen] = useState(false);

  // Cerrar App Launcher con ESC
  useEffect(() => {
    if (!launcherOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setLauncherOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [launcherOpen]);
  
  // Definir pestañas según rol de usuario — Categorizado tipo Odoo
  const allTabs = [
    { id: 'dashboard', label: 'Dashboard', icon: '📈', category: 'Analytics', roles: ['admin', 'owner', 'consultor', 'negociador', 'vnc'] },
    { id: 'competitividad', label: 'Competitividad', icon: '📊', category: 'Analytics', roles: ['admin', 'owner', 'consultor', 'regionales', 'regionales_lab'] },
    { id: 'recomendaciones-ia', label: 'Recomendaciones IA', icon: '🤖', category: 'Analytics', roles: ['admin', 'owner', 'consultor', 'negociador'] },
    { id: 'captura-dashboard', label: 'Dashboard Captura', icon: '📉', category: 'Analytics', roles: ['admin', 'owner', 'consultor', 'investigador', 'capturador', 'regionales', 'regionales_lab'] },
    { id: 'lab-promo', label: 'Lab Promociones', icon: '🧪', category: 'Ventas & Promos', roles: ['admin', 'owner', 'consultor', 'negociador', 'negociador_lab', 'regionales_lab'] },
    { id: 'distribucion', label: 'Simulador Distribución', icon: '🧮', category: 'Ventas & Promos', roles: ['admin', 'owner', 'consultor', 'negociador'] },
    { id: 'simulacion-venta', label: 'Simulación Venta', icon: '💰', category: 'Ventas & Promos', roles: ['admin', 'owner', 'consultor', 'negociador', 'negociador_lab', 'capturador', 'investigador', 'regionales', 'regionales_lab', 'vnc'] },
    { id: 'dispersion-precios', label: 'Simulador Dispersión', icon: '🎯', category: 'Ventas & Promos', roles: ['admin', 'owner', 'consultor', 'rentabilidad', 'negociador'] },
    { id: 'analisis-margenes', label: 'Análisis Márgenes', icon: '⚠️', category: 'Ventas & Promos', roles: ['admin', 'owner', 'consultor', 'rentabilidad', 'negociador'] },
    { id: 'guia-simulada', label: 'Guía Simulada', icon: '📋', category: 'Ventas & Promos', roles: ['admin', 'owner', 'consultor', 'rentabilidad', 'negociador'] },
    { id: 'promo-politica', label: 'Promo vs Política', icon: '⚖️', category: 'Ventas & Promos', roles: ['admin', 'owner', 'consultor', 'rentabilidad', 'negociador'] },
    { id: 'guia-competencia', label: 'Guía vs Competencia', icon: '🏷️', category: 'Ventas & Promos', roles: ['admin', 'owner', 'consultor', 'rentabilidad', 'negociador'] },
    { id: 'pmt-promo', label: 'Importador PMT', icon: '📦', category: 'Ventas & Promos', roles: ['admin', 'owner', 'rentabilidad'] },
    { id: 'captura', label: 'Captura Masiva', icon: '📝', category: 'Data', roles: ['admin', 'investigador', 'capturador', 'owner'] },
    { id: 'maestros', label: 'Maestros', icon: '📁', category: 'Data', roles: ['admin', 'owner'] },
    { id: 'laboratorio', label: 'Laboratorio', icon: '🔬', category: 'Data', roles: ['admin', 'owner', 'consultor'] },
    { id: 'descargas', label: 'Descargas', icon: '📥', category: 'Data', roles: ['admin', 'owner', 'consultor'] },
    { id: 'revionics', label: 'Integración Revionics', icon: '🔗', category: 'Data', roles: ['admin', 'owner', 'rentabilidad'] },
    { id: 'gestor-usuarios', label: 'Gestor Usuarios', icon: '👥', category: 'Admin', roles: ['admin', 'owner'] },
    { id: 'admin-db', label: 'Admin BD', icon: '🗄️', category: 'Admin', roles: ['admin', 'owner'] },
    { id: 'uploads-admin', label: 'Auditoría Archivos', icon: '🗂️', category: 'Admin', roles: ['admin', 'owner'] },
  ];
  const CATEGORY_ORDER = ['Analytics', 'Ventas & Promos', 'Data', 'Admin'];
  const CATEGORY_META = {
    'Analytics':        { color: 'from-sky-500 to-cyan-500',        bg: 'bg-sky-50 border-sky-200 hover:bg-sky-100',       text: 'text-sky-800',        emoji: '📊' },
    'Ventas & Promos':  { color: 'from-emerald-500 to-teal-500',    bg: 'bg-emerald-50 border-emerald-200 hover:bg-emerald-100', text: 'text-emerald-800',    emoji: '💰' },
    'Data':             { color: 'from-amber-500 to-orange-500',    bg: 'bg-amber-50 border-amber-200 hover:bg-amber-100', text: 'text-amber-800',      emoji: '🗂️' },
    'Admin':            { color: 'from-slate-500 to-slate-700',     bg: 'bg-slate-50 border-slate-200 hover:bg-slate-100', text: 'text-slate-800',      emoji: '⚙️' },
  };
  
  const userRole = user?.role || 'user';
  // UNIÓN: cualquier tab permitido por el rol (hardcoded) O por allowed_tabs del backend.
  // Así los tabs NUEVOS (agregados después de crear el rol en BD) aparecen automáticamente.
  const userAllowedTabs = Array.isArray(user?.allowed_tabs) ? user.allowed_tabs : null;
  const availableTabs = allTabs.filter((tab) =>
    tab.roles.includes(userRole) || (userAllowedTabs && userAllowedTabs.includes(tab.id))
  );
  
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      verifyToken(token);
    }
  }, []);
  
  // Redirigir a pestaña disponible cuando el usuario se autentica.
  // 'home' (App Launcher) es siempre válido; para otros tabs verifica UNIÓN de rol y allowed_tabs.
  useEffect(() => {
    if (isAuthenticated && user) {
      const role = user.role || 'user';
      const allowed = Array.isArray(user.allowed_tabs) ? user.allowed_tabs : null;
      const tabs = allTabs.filter((tab) =>
        tab.roles.includes(role) || (allowed && allowed.includes(tab.id))
      );
      const tabAvailable = activeTab === 'home' || tabs.find((t) => t.id === activeTab);
      if (!tabAvailable && tabs.length > 0) {
        setActiveTab('home');
      }
    }
  }, [isAuthenticated, user]);
  
  // CARGA AUTOMÁTICA DE MAESTROS para Captura Masiva y Seguimiento
  useEffect(() => {
    if (isAuthenticated && (activeTab === 'captura' || activeTab === 'seguimiento')) {
      cargarMaestrosAutomatico();
    }
  }, [isAuthenticated, activeTab]);
  
  const cargarMaestrosAutomatico = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      setMaestrosCargando(true);
      const res = await fetch(`${API}/api/maestros/cargar-automatico`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (res.ok) {
        const data = await res.json();
        console.log('✅ Maestros cargados automáticamente:', data);
      }
    } catch (err) {
      console.error('Error cargando maestros:', err);
    } finally {
      setMaestrosCargando(false);
    }
  };
  
  const verifyToken = async (token) => {
    try {
      const res = await fetch(`${API}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUser(data);
        setIsAuthenticated(true);
      } else {
        localStorage.removeItem('token');
      }
    } catch (err) {
      localStorage.removeItem('token');
    }
  };
  
  const handleLogout = () => {
    localStorage.removeItem('token');
    setIsAuthenticated(false);
    setUser(null);
    setActiveTab('home');
  };
  
  if (!isAuthenticated) {
    return <LoginForm onLogin={(u) => { setUser(u); setIsAuthenticated(true); }} />;
  }
  
  // Renderizado especial para perfil comfarmalsa
  if (userRole === 'comfarmalsa') {
    return (
      <Suspense fallback={<ModuloCargando />}>
        <ComfarmalsaApp user={user} onLogout={handleLogout} />
      </Suspense>
    );
  }
  
  // Renderizado especial para perfil negociador_lab - Solo Lab Promociones
  if (userRole === 'negociador_lab') {
    return (
      <div className="min-h-screen bg-gray-50">
        <Toaster position="top-right" richColors />
        
        {/* Header simplificado para Negociador */}
        <header className="bg-gradient-to-r from-emerald-800 to-emerald-600 text-white shadow-lg">
          <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold">🧪 Laboratorio de Promociones</h1>
              <p className="text-emerald-200 text-sm">Análisis de Competitividad y Simulación</p>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm">👤 {user?.username}</span>
              <button 
                onClick={handleLogout}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-sm font-medium transition-colors"
              >
                Cerrar Sesión
              </button>
            </div>
          </div>
        </header>
        
        {/* Main Content - Solo Lab Promociones */}
        <main className="max-w-7xl mx-auto px-4 py-6">
          <Suspense fallback={<ModuloCargando />}>
            <LabPromoModule />
          </Suspense>
        </main>
        
        {/* Footer */}
        <footer className="bg-gray-800 text-gray-400 py-4 text-center text-sm">
          Laboratorio de Promociones - Analytica v{APP_VERSION}
        </footer>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster position="top-right" richColors />

      {/* Overlay del App Launcher tipo Odoo (grid modal) */}
      {launcherOpen && (
        <div
          className="fixed inset-0 z-[60] bg-slate-900/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto"
          onClick={() => setLauncherOpen(false)}
          data-testid="app-launcher-overlay"
        >
          <div
            className="w-full max-w-6xl mt-8 mb-8 mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-gradient-to-r from-indigo-50 to-white">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-600 to-purple-600 text-white flex items-center justify-center">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/></svg>
                </div>
                <div>
                  <h3 className="font-bold text-slate-800 text-base">Todas las Aplicaciones</h3>
                  <p className="text-[11px] text-slate-500">{availableTabs.length} apps disponibles · Presiona ESC o clic fuera para cerrar</p>
                </div>
              </div>
              <button
                onClick={() => setLauncherOpen(false)}
                className="w-9 h-9 rounded-lg hover:bg-slate-100 text-slate-500 flex items-center justify-center text-xl"
                title="Cerrar"
                data-testid="app-launcher-close"
              >✕</button>
            </div>
            <div className="p-5 max-h-[75vh] overflow-y-auto">
              <AppLauncher
                availableTabs={availableTabs}
                categoryOrder={CATEGORY_ORDER}
                categoryMeta={CATEGORY_META}
                onOpen={(id) => { setActiveTab(id); setLauncherOpen(false); }}
                user={user}
                hideWelcome
              />
            </div>
          </div>
        </div>
      )}

      {/* Main Content Area — sin sidebar, full width */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top Navbar tipo Odoo */}
        <header className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-40" data-testid="odoo-topbar">
          <div className="px-4 md:px-6 h-14 flex items-center gap-3">
            {/* Grid launcher button (tipo Odoo) */}
            <button
              onClick={() => setLauncherOpen(true)}
              className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white flex items-center justify-center shadow-sm transition-all"
              title="Todas las aplicaciones"
              data-testid="odoo-launcher-btn"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <rect x="3" y="3" width="7" height="7" rx="1.5"/>
                <rect x="14" y="3" width="7" height="7" rx="1.5"/>
                <rect x="3" y="14" width="7" height="7" rx="1.5"/>
                <rect x="14" y="14" width="7" height="7" rx="1.5"/>
              </svg>
            </button>

            {/* Logo + version */}
            <div className="flex items-center gap-2 border-r border-slate-200 pr-3">
              <img src="/logo-difare.png" alt="DIFARE" className="h-7 w-auto object-contain" />
              <span className="text-[10px] text-slate-400 font-mono">v{APP_VERSION}</span>
            </div>

            {/* Breadcrumb: Inicio / [app actual] */}
            <nav className="flex items-center gap-2 text-sm flex-1 min-w-0" data-testid="odoo-breadcrumb">
              <button
                onClick={() => setActiveTab('home')}
                className={`flex items-center gap-1 px-2 py-1 rounded hover:bg-slate-100 transition-colors ${activeTab === 'home' ? 'text-indigo-700 font-semibold' : 'text-slate-500'}`}
                data-testid="odoo-home-btn"
              >
                <span>🏠</span>
                <span className="hidden sm:inline">Inicio</span>
              </button>
              {activeTab !== 'home' && (
                <>
                  <span className="text-slate-300">/</span>
                  <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                    {availableTabs.find((t) => t.id === activeTab)?.category || ''}
                  </span>
                  <span className="text-slate-300">/</span>
                  <div className="flex items-center gap-2 px-2 py-1 rounded bg-indigo-50 text-indigo-800 border border-indigo-200 min-w-0">
                    <span>{availableTabs.find((t) => t.id === activeTab)?.icon}</span>
                    <span className="font-semibold text-sm truncate">{availableTabs.find((t) => t.id === activeTab)?.label}</span>
                  </div>
                </>
              )}
            </nav>

            {/* Fecha (opcional, en desktop) */}
            <div className="hidden lg:block text-[11px] text-slate-500 border-r border-slate-200 pr-3">
              {new Date().toLocaleDateString('es-EC', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </div>

            {/* User + Logout */}
            <div className="flex items-center gap-2">
              <div className="hidden md:block text-right leading-tight">
                <div className="text-xs font-semibold text-slate-700">{user?.username}</div>
                <div className="text-[10px] text-slate-400 uppercase tracking-wider">{userRole}</div>
              </div>
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center font-bold text-sm">
                {(user?.username || '?').charAt(0).toUpperCase()}
              </div>
              <button
                onClick={handleLogout}
                className="ml-1 px-3 py-1.5 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-700 text-xs font-semibold transition-colors flex items-center gap-1"
                title="Cerrar sesión"
                data-testid="odoo-logout-btn"
              >
                <span>🚪</span>
                <span className="hidden sm:inline">Salir</span>
              </button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 p-4 md:p-6">
          <Suspense fallback={<ModuloCargando />}>
          {activeTab === 'home' && (
            <AppLauncher
              availableTabs={availableTabs}
              categoryOrder={CATEGORY_ORDER}
              categoryMeta={CATEGORY_META}
              onOpen={(id) => setActiveTab(id)}
              user={user}
            />
          )}
          {activeTab === 'dashboard' && <DashboardTab />}
          {activeTab === 'maestros' && <MaestrosTab />}
          {activeTab === 'captura' && <CapturaMasivaTab userRole={userRole} />}
          {activeTab === 'captura-dashboard' && <CapturaDashboard />}
          {activeTab === 'seguimiento' && <SeguimientoTab />}
          {activeTab === 'lab-promo' && <LabPromoModule />}
          {activeTab === 'distribucion' && <DistribucionModule />}
          {activeTab === 'simulacion-venta' && <SimulacionVentaModule user={user} userRole={userRole} />}
          {/* asistente-competencia integrado como sub-tab en Competitividad */}
          {activeTab === 'gestor-usuarios' && <GestorUsuariosModule />}
          {activeTab === 'competitividad' && <CompetitividadModule userRole={userRole} allowedTabs={userAllowedTabs} />}
          {activeTab === 'recomendaciones-ia' && <RecomendacionesIAModule />}
          {activeTab === 'laboratorio' && <LaboratorioTab />}
          {activeTab === 'descargas' && <DescargasTab />}
          {activeTab === 'admin-db' && <AdminDBModule />}
          {activeTab === 'pmt-promo' && <PMTPromocionesModule />}
          {activeTab === 'dispersion-precios' && <SimuladorDispersionModule />}
          {activeTab === 'analisis-margenes' && <AnalisisMargenesModule />}
          {activeTab === 'guia-simulada' && <GuiaSimuladaModule />}
          {activeTab === 'promo-politica' && <PromoPoliticaModule />}
          {activeTab === 'revionics' && <RevionicsModule />}
          {activeTab === 'guia-competencia' && <GuiaCompetenciaModule />}
          {activeTab === 'uploads-admin' && <UploadsAdminModule />}
          </Suspense>
        </main>
        
        {/* Footer */}
        <footer className="bg-white border-t py-4 text-center text-sm text-gray-500">
          DIFARE - Sistema Analítico Integral v{APP_VERSION} © {new Date().getFullYear()}
        </footer>
      </div>
    </div>
  );
}


// ============================================
// LOGIN FORM - GRUPO DIFARE
// ============================================

// Componente de iconos flotantes animados para el fondo
const FloatingAnalyticsIcons = () => {
  const iconPaths = [
    // BarChart
    "M18 20V10M12 20V4M6 20V14",
    // TrendingUp
    "M22 7L13.5 15.5L8.5 10.5L2 17",
    // PieChart
    "M21.21 15.89A10 10 0 1 1 8 2.83M22 12A10 10 0 0 0 12 2v10z",
    // Activity
    "M22 12h-4l-3 9L9 3l-3 9H2",
    // LineChart
    "M3 3v18h18M19 9l-5 5-4-4-3 3",
  ];
  
  const positions = [
    { left: '5%', top: '10%', delay: '0s', size: 40 },
    { left: '85%', top: '15%', delay: '2s', size: 50 },
    { left: '15%', top: '70%', delay: '4s', size: 45 },
    { left: '75%', top: '60%', delay: '1s', size: 55 },
    { left: '90%', top: '40%', delay: '3s', size: 35 },
    { left: '8%', top: '45%', delay: '5s', size: 42 },
    { left: '70%', top: '85%', delay: '2.5s', size: 48 },
    { left: '25%', top: '25%', delay: '4.5s', size: 38 },
    { left: '60%', top: '8%', delay: '1.5s', size: 52 },
    { left: '40%', top: '80%', delay: '3.5s', size: 44 },
  ];

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <style>{`
        @keyframes float-gentle {
          0%, 100% {
            transform: translateY(0px) translateX(0px) rotate(0deg);
            opacity: 0.04;
          }
          25% {
            transform: translateY(-20px) translateX(10px) rotate(5deg);
            opacity: 0.06;
          }
          50% {
            transform: translateY(-10px) translateX(-10px) rotate(-3deg);
            opacity: 0.04;
          }
          75% {
            transform: translateY(-25px) translateX(5px) rotate(3deg);
            opacity: 0.05;
          }
        }
      `}</style>
      {positions.map((pos, index) => (
        <svg
          key={index}
          className="absolute text-white"
          style={{
            left: pos.left,
            top: pos.top,
            width: pos.size,
            height: pos.size,
            animation: `float-gentle 20s ease-in-out infinite`,
            animationDelay: pos.delay,
            opacity: 0.04,
          }}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d={iconPaths[index % iconPaths.length]} />
        </svg>
      ))}
    </div>
  );
};

function LoginForm({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const res = await fetch(`${API}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
      
      const data = await res.json();
      
      if (res.ok && data.token) {
        localStorage.setItem('token', data.token);
        onLogin(data.user);
        toast.success('¡Bienvenido!');
      } else {
        setError(data.detail || 'Credenciales inválidas');
      }
    } catch (err) {
      setError('Error de conexión');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 flex items-center justify-center p-4 relative">
      {/* Iconos animados de fondo */}
      <FloatingAnalyticsIcons />
      
      {/* Card de login */}
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md relative z-10">
        <div className="text-center mb-8">
          {/* Logo de DIFARE */}
          <div className="flex justify-center mb-4">
            <img 
              src="/logo-difare.png" 
              alt="Grupo DIFARE" 
              className="h-20 w-auto object-contain"
              data-testid="login-logo"
            />
          </div>
          <p className="text-gray-500 text-sm font-medium tracking-wide">
            Sistema Analítico Integral
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Usuario</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-gray-50"
              placeholder="Ingrese su usuario"
              required
              data-testid="login-username-input"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-gray-50"
              placeholder="Ingrese su contraseña"
              required
              data-testid="login-password-input"
            />
          </div>
          
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm" data-testid="login-error">
              {error}
            </div>
          )}
          
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-green-700 hover:bg-green-800 text-white font-semibold rounded-lg transition-all duration-200 disabled:bg-gray-400 shadow-lg hover:shadow-xl"
            data-testid="login-submit-btn"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                </svg>
                Ingresando...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <circle cx="12" cy="12" r="6" />
                  <circle cx="12" cy="12" r="2" />
                </svg>
                Ingresar
              </span>
            )}
          </button>
        </form>
        
        {/* Línea decorativa con colores de DIFARE */}
        <div className="mt-6 flex justify-center gap-1">
          <div className="w-8 h-1 bg-green-700 rounded"></div>
          <div className="w-8 h-1 bg-green-500 rounded"></div>
          <div className="w-8 h-1 bg-green-300 rounded"></div>
        </div>
      </div>
    </div>
  );
}


// ============================================
// DASHBOARD TAB
// ============================================
function DashboardTab() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchStatus();
  }, []);
  
  const fetchStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/maestros/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setStatus(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return <div className="text-center py-12">⏳ Cargando...</div>;
  }
  
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
        <h2 className="text-2xl font-bold text-blue-800 mb-2">📈 Dashboard</h2>
        <p className="text-blue-600">Estado del sistema y resumen de datos</p>
      </div>
      
      {/* Estado de Maestros */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {status && Object.entries(status).map(([key, value]) => (
          <div 
            key={key}
            className={`rounded-xl p-4 border ${
              value.loaded ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
            }`}
          >
            <h3 className="font-semibold text-gray-800 text-sm mb-2">
              {key.replace(/_/g, ' ').toUpperCase()}
            </h3>
            <div className="flex items-center justify-between">
              <span className={`text-2xl font-bold ${value.loaded ? 'text-green-600' : 'text-red-600'}`}>
                {value.loaded ? value.registros?.toLocaleString() : '0'}
              </span>
              <span className={`px-2 py-1 rounded text-xs font-medium ${
                value.loaded ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'
              }`}>
                {value.loaded ? '✅ Cargado' : '❌ Pendiente'}
              </span>
            </div>
          </div>
        ))}
      </div>
      
      {/* Guía rápida */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h3 className="text-lg font-bold text-gray-800 mb-4">🚀 Guía Rápida</h3>
        <ol className="space-y-3 text-gray-700">
          <li className="flex items-start gap-3">
            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm font-bold">1</span>
            <span><strong>Maestros:</strong> Cargue los archivos de Maestro Negociación, Costo Franquicia y Guía Comercial</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm font-bold">2</span>
            <span><strong>Informe:</strong> Cargue el Informe de Investigación (base para duplicidad)</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm font-bold">3</span>
            <span><strong>Pipeline:</strong> Ejecute el pipeline completo (Duplicidad → Paso3 → Paso4 → Paso5)</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm font-bold">4</span>
            <span><strong>Descargas:</strong> Descargue los resultados en Excel</span>
          </li>
        </ol>
      </div>
    </div>
  );
}


// ============================================
// MAESTROS TAB
// ============================================
function MaestrosTab() {
  const [uploading, setUploading] = useState({});
  const [status, setStatus] = useState(null);
  const [excelMaestroStatus, setExcelMaestroStatus] = useState(null);
  const [exportingData, setExportingData] = useState(false);
  const [resumenCapturas, setResumenCapturas] = useState(null);
  const [viewingMaestro, setViewingMaestro] = useState(null);
  const [maestroData, setMaestroData] = useState(null);
  const [loadingData, setLoadingData] = useState(false);
  
  // Estados para gestión de cargas de guía comercial
  const [showGuiaCargas, setShowGuiaCargas] = useState(false);
  const [guiaCargas, setGuiaCargas] = useState([]);
  const [loadingGuiaCargas, setLoadingGuiaCargas] = useState(false);
  const [deletingCarga, setDeletingCarga] = useState(null);
  
  useEffect(() => {
    fetchStatus();
    fetchExcelMaestroStatus();
    fetchResumenCapturas();
  }, []);
  
  const fetchStatus = async () => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API}/api/maestros/status`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    setStatus(await res.json());
  };
  
  const fetchExcelMaestroStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/excel-maestro/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setExcelMaestroStatus(await res.json());
      }
    } catch (err) {
      console.error(err);
    }
  };
  
  const fetchResumenCapturas = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/capturas/resumen`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setResumenCapturas(await res.json());
      }
    } catch (err) {
      console.error(err);
    }
  };
  
  // Funciones para gestión de cargas de guía comercial
  const fetchGuiaCargas = async () => {
    setLoadingGuiaCargas(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/guia-comercial/cargas`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setGuiaCargas(data.cargas || []);
      } else {
        toast.error('Error al cargar historial de cargas');
      }
    } catch (err) {
      toast.error('Error de conexión');
    }
    setLoadingGuiaCargas(false);
  };
  
  const deleteGuiaCarga = async (fechaCarga) => {
    if (!window.confirm(`¿Está seguro de eliminar la carga del ${fechaCarga}?\nEsta acción no se puede deshacer.`)) {
      return;
    }
    
    setDeletingCarga(fechaCarga);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/guia-comercial/cargas/${encodeURIComponent(fechaCarga)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        toast.success(`✅ Eliminados ${data.registros_eliminados} registros`);
        fetchGuiaCargas();
        fetchStatus(); // Actualizar el contador de registros
      } else {
        toast.error(data.detail || 'Error al eliminar carga');
      }
    } catch (err) {
      toast.error('Error de conexión');
    }
    setDeletingCarga(null);
  };
  
  const viewMaestroData = async (tipo) => {
    if (viewingMaestro === tipo) {
      setViewingMaestro(null);
      setMaestroData(null);
      return;
    }
    
    setLoadingData(true);
    setViewingMaestro(tipo);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/dataframes/${tipo}?limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMaestroData(data);
      } else {
        toast.error('Error al cargar datos');
      }
    } catch (err) {
      toast.error('Error de conexión');
    }
    setLoadingData(false);
  };
  
  const handleUpload = async (tipo, file) => {
    setUploading(prev => ({ ...prev, [tipo]: true }));
    toast.loading(`Cargando ${tipo}...`, { id: `upload-${tipo}` });

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      // 1) Kick off the async job — retorna job_id casi inmediato
      const res = await fetch(`${API}/api/maestros/upload/${tipo}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      if (!res.ok) {
        const contentType = (res.headers.get('content-type') || '').toLowerCase();
        let errorMsg = `Error ${res.status}`;
        if (contentType.includes('application/json')) {
          const err = await res.json();
          errorMsg = err.detail || errorMsg;
        }
        toast.error(errorMsg, { id: `upload-${tipo}` });
        return;
      }
      const startData = await res.json();
      const jobId = startData.job_id;
      if (!jobId) {
        toast.error('No se recibió job_id del servidor', { id: `upload-${tipo}` });
        return;
      }

      // 2) Poll cada 1.5s hasta status completed/failed. Max 20 min.
      // - 404 se tolera hasta MAX_404_RETRIES consecutivos (race condition
      //   normal justo tras el POST). Después se considera fallo definitivo:
      //   el job no existe o fue purgado por TTL.
      // - Otros errores HTTP son transitorios (5xx/red) y se reintentan.
      const deadline = Date.now() + 20 * 60 * 1000;
      const MAX_404_RETRIES = 5;   // ~7.5s de tolerancia
      let consecutive404 = 0;
      let lastMsg = '';
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (Date.now() > deadline) {
          toast.error('Timeout: 20 min sin completar', { id: `upload-${tipo}` });
          return;
        }
        await new Promise(r => setTimeout(r, 1500));
        const statRes = await fetch(`${API}/api/maestros/upload-status/${jobId}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (statRes.status === 404) {
          consecutive404 += 1;
          if (consecutive404 >= MAX_404_RETRIES) {
            toast.error(
              `❌ Job ${jobId} no existe en el servidor (404 persistente). ` +
              `Es posible que el worker haya reiniciado antes de crearlo, ` +
              `que el TTL lo haya purgado o que el enrutamiento falló.`,
              { id: `upload-${tipo}`, duration: 8000 },
            );
            return;
          }
          continue;
        }
        // Cualquier otro error HTTP: reintentar silenciosamente y resetear el
        // contador de 404 (fue un fallo distinto, no un "job no existe").
        if (!statRes.ok) {
          consecutive404 = 0;
          continue;
        }
        consecutive404 = 0;
        const st = await statRes.json();
        const msg = `${st.progress || 0}% — ${st.message || st.phase}`;
        if (msg !== lastMsg) {
          toast.loading(msg, { id: `upload-${tipo}` });
          lastMsg = msg;
        }
        if (st.status === 'completed') {
          const r = st.result || {};
          toast.success(
            `✅ ${tipo}: ${r.registros?.toLocaleString?.() || r.registros} filas` +
            (r.registros_replicados ? ` (→ df_maestro replicado)` : ''),
            { id: `upload-${tipo}`, duration: 5000 },
          );
          fetchStatus();
          return;
        }
        if (st.status === 'failed') {
          toast.error(`❌ ${st.error || st.message || 'Error desconocido'}`, {
            id: `upload-${tipo}`, duration: 8000,
          });
          return;
        }
      }
    } catch (err) {
      toast.error(`Error: ${err?.message || err}`, { id: `upload-${tipo}` });
    } finally {
      setUploading(prev => ({ ...prev, [tipo]: false }));
    }
  };
  
  const handleExcelMaestroUpload = async (file) => {
    setUploading(prev => ({ ...prev, excel_maestro: true }));
    toast.loading('Cargando Excel Maestro... esto puede tomar unos minutos', { id: 'upload-excel' });
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      
      // Timeout de 10 minutos para Excel Maestro grande
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 600000);
      
      const res = await fetch(`${API}/api/excel-maestro/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!res.ok) {
        let errorMsg = `Error ${res.status}`;
        if (res.status === 504 || res.status === 502) {
          errorMsg = 'Timeout del servidor. El archivo es muy grande.';
        }
        toast.error(errorMsg, { id: 'upload-excel' });
        return;
      }
      
      const data = await res.json();
      
      if (data.success) {
        toast.success(`✅ Excel Maestro cargado: ${data.registros} registros, ${data.pos_count} POS`, { id: 'upload-excel' });
        fetchExcelMaestroStatus();
      } else {
        toast.error(data.detail || 'Error al cargar Excel Maestro', { id: 'upload-excel' });
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        toast.error('Timeout: el archivo tardó demasiado.', { id: 'upload-excel' });
      } else {
        toast.error('Error de conexión', { id: 'upload-excel' });
      }
    } finally {
      setUploading(prev => ({ ...prev, excel_maestro: false }));
    }
  };
  
  const handleInformeUpload = async (file) => {
    setUploading(prev => ({ ...prev, informe: true }));
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await fetch(`${API}/api/informe-investigacion/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        toast.success(`✅ Informe cargado: ${data.estadisticas.registros} registros`);
        fetchStatus();
      } else {
        toast.error(data.detail || 'Error al cargar');
      }
    } catch (err) {
      toast.error('Error de conexión');
    } finally {
      setUploading(prev => ({ ...prev, informe: false }));
    }
  };
  
  // Exportar datos de capturas para backup/migración
  const exportCapturas = async (incluirInforme = false) => {
    setExportingData(true);
    try {
      const token = localStorage.getItem('token');
      const url = incluirInforme 
        ? `${API}/api/investigacion/export-backup?incluir_informe=true`
        : `${API}/api/investigacion/export-backup`;
      
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `backup_capturas_${new Date().toISOString().slice(0,10)}.json`;
        a.click();
        toast.success('✅ Backup exportado correctamente');
      } else {
        const text = await res.text();
        if (text.includes('503')) {
          toast.error('El servidor está ocupado. Espere 30 segundos e intente de nuevo.');
        } else {
          toast.error('Error al exportar backup');
        }
      }
    } catch (err) {
      toast.error('Error de conexión. Intente de nuevo.');
    } finally {
      setExportingData(false);
    }
  };
  
  // Importar datos de capturas desde backup
  const importCapturas = async (file) => {
    setUploading(prev => ({ ...prev, backup: true }));
    try {
      const token = localStorage.getItem('token');
      const content = await file.text();
      
      const res = await fetch(`${API}/api/investigacion/import-backup`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: content
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        toast.success(`✅ Importados ${data.imported} registros`);
      } else {
        toast.error(data.detail || 'Error al importar');
      }
    } catch (err) {
      toast.error('Error de conexión');
    } finally {
      setUploading(prev => ({ ...prev, backup: false }));
    }
  };
  
  const maestros = [
    // v24.0.0: `maestro_negociacion` DESACTIVADO desde esta pantalla.
    // La carga oficial se hace en Admin BD → tarjeta "Maestro Negociación (v24)".
    { tipo: 'maestro_negociacion', label: 'Maestro Negociación', icon: '📋', disabled: true, moved_to: 'Admin BD' },
    { tipo: 'costo_franquicia', label: 'Costo Franquicia', icon: '💰' },
    { tipo: 'guia_comercial', label: 'Guía Comercial', icon: '📑' }
  ];

  // === v22.0.0 — Maestro Estrategias (repositorio independiente) ===
  // Sube el archivo con IDMATERIAL + 8 columnas de estrategia. El backend
  // hace JOIN dinámico en /maestros/vista y /asistente-competencia/buscar-productos.
  const [estrStatus, setEstrStatus] = React.useState({ registros: 0, sample: [] });
  const [estrUploading, setEstrUploading] = React.useState(false);
  const fetchEstrStatus = React.useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/maestros/estrategias/status`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (r.ok) setEstrStatus(await r.json());
    } catch { /* silent */ }
  }, []);
  React.useEffect(() => { fetchEstrStatus(); }, [fetchEstrStatus]);
  const handleEstrUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setEstrUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${API}/api/maestros/cargar-estrategias`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: fd,
      });
      const data = await r.json();
      if (r.ok && data.success) {
        toast.success(`✅ Estrategias cargadas: ${data.registros} filas`);
        fetchEstrStatus();
      } else {
        toast.error(data.detail || 'Error al cargar Estrategias');
      }
    } catch {
      toast.error('Error de conexión');
    } finally {
      setEstrUploading(false);
      e.target.value = '';
    }
  };
  const MaestroEstrategiasCard = () => (
    <div
      className="bg-purple-50 border-2 border-purple-200 rounded-xl p-4"
      data-testid="maestro-estrategias-card"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-bold text-purple-800 mb-1">
            🎯 Maestro Estrategias
          </h3>
          <p className="text-sm text-purple-700">
            Archivo con <b>IDMATERIAL</b> y las 8 columnas de estrategia (ESTRATEGIA CA, ESTRATEGIA PH,
            BOSTON, ROLE ESTRATEGIA, SENSIBILIDAD, UNIDADES CAFÉ / CAFI / PH). Se cruza dinámicamente
            con el Maestro Negociación por IDMATERIAL.
          </p>
          <p className="text-xs text-purple-600 mt-1">
            📊 <b>{estrStatus.registros?.toLocaleString?.() || 0}</b> registros cargados
          </p>
        </div>
        <label
          className={`px-3 py-2 rounded-lg text-sm font-semibold text-white cursor-pointer ${
            estrUploading ? 'bg-gray-400' : 'bg-purple-600 hover:bg-purple-700'
          }`}
          data-testid="btn-upload-estrategias"
        >
          {estrUploading ? '⏳ Subiendo...' : '📤 Cargar Estrategias'}
          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            disabled={estrUploading}
            onChange={handleEstrUpload}
          />
        </label>
      </div>
    </div>
  );
  
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6">
        <h2 className="text-2xl font-bold text-green-800 mb-2">📁 Carga de Archivos Maestros</h2>
        <p className="text-green-600">Cargue los archivos necesarios para el análisis</p>
      </div>
      
      {/* Maestros */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {maestros.map(m => (
          <div key={m.tipo} className={`bg-white border rounded-xl p-4 ${m.disabled ? 'border-amber-200 bg-amber-50/40' : 'border-gray-200'}`}>
            <h3 className="font-bold text-gray-800 mb-3">{m.icon} {m.label}</h3>

            {status?.[m.tipo]?.cache_loaded && (
              <div className="mb-3 text-sm text-green-600">
                ✅ {status[m.tipo].cache_registros?.toLocaleString()} registros
                {status[m.tipo].sincronizado && <span className="ml-2 text-xs text-blue-500">💾 Sincronizado</span>}
              </div>
            )}

            {m.disabled ? (
              <div className="p-3 rounded-lg border border-amber-300 bg-amber-50 text-amber-900 text-sm">
                <b>Movido a {m.moved_to}.</b><br />
                A partir de v24 este cargador se centralizó en <b>Admin BD → Maestro Negociación (v24)</b>
                con streaming, chunks reales, UPSERT inmediato y reanudación.
              </div>
            ) : (
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={(e) => e.target.files[0] && handleUpload(m.tipo, e.target.files[0])}
                className="w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                disabled={uploading[m.tipo]}
                data-testid={`maestros-upload-${m.tipo}`}
              />
            )}
            {uploading[m.tipo] && <p className="text-sm text-gray-500 mt-2">⏳ Cargando...</p>}
            
            {/* Botón ver datos */}
            {status?.[m.tipo]?.cache_registros > 0 && (
              <button
                onClick={() => viewMaestroData(m.tipo)}
                className={`mt-3 w-full px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                  viewingMaestro === m.tipo 
                    ? 'bg-blue-100 border-blue-300 text-blue-700' 
                    : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                }`}
              >
                {viewingMaestro === m.tipo ? '📊 Ocultar datos' : '👁️ Ver datos'}
              </button>
            )}
            
            {/* Botón gestionar cargas (solo para guía comercial) */}
            {m.tipo === 'guia_comercial' && status?.[m.tipo]?.cache_registros > 0 && (
              <button
                onClick={() => { setShowGuiaCargas(!showGuiaCargas); if (!showGuiaCargas) fetchGuiaCargas(); }}
                className={`mt-2 w-full px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                  showGuiaCargas
                    ? 'bg-red-100 border-red-300 text-red-700'
                    : 'bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100'
                }`}
              >
                {showGuiaCargas ? '✕ Cerrar gestión' : '🗑️ Gestionar cargas'}
              </button>
            )}
          </div>
        ))}
      </div>

      {/* ==================== MAESTRO ESTRATEGIAS (v22.0.0) ==================== */}
      <MaestroEstrategiasCard />

      {/* Panel de gestión de cargas de guía comercial */}
      {showGuiaCargas && (
        <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-4 mt-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-bold text-orange-800">
              🗑️ Gestión de Cargas - Guía Comercial
            </h3>
            <button 
              onClick={() => setShowGuiaCargas(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>
          
          <p className="text-sm text-orange-700 mb-4">
            Seleccione una carga para eliminar todos sus registros. Esta acción no se puede deshacer.
          </p>
          
          {loadingGuiaCargas ? (
            <div className="text-center py-4 text-gray-500">⏳ Cargando historial...</div>
          ) : guiaCargas.length > 0 ? (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {guiaCargas.map((carga, idx) => (
                <div key={idx} className="flex items-center justify-between bg-white rounded-lg p-3 border border-orange-200">
                  <div>
                    <p className="font-medium text-gray-800">
                      📅 {new Date(carga.fecha_carga).toLocaleString('es-EC')}
                    </p>
                    <p className="text-sm text-gray-500">
                      {carga.registros.toLocaleString()} registros • Usuario: {carga.usuario}
                    </p>
                  </div>
                  <button
                    onClick={() => deleteGuiaCarga(carga.fecha_carga)}
                    disabled={deletingCarga === carga.fecha_carga}
                    className="px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-sm rounded-lg disabled:bg-gray-400 transition-colors"
                  >
                    {deletingCarga === carga.fecha_carga ? '⏳' : '🗑️'} Eliminar
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-4">No hay cargas registradas con fecha</p>
          )}
        </div>
      )}
      
      {/* Visualización de datos del maestro seleccionado */}
      {viewingMaestro && (
        <div className="bg-white border-2 border-blue-200 rounded-xl p-4 mt-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-bold text-blue-800">
              📊 Datos de {maestros.find(m => m.tipo === viewingMaestro)?.label || viewingMaestro}
            </h3>
            <button 
              onClick={() => { setViewingMaestro(null); setMaestroData(null); }}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕ Cerrar
            </button>
          </div>
          
          {loadingData ? (
            <div className="text-center py-8 text-gray-500">⏳ Cargando datos...</div>
          ) : maestroData?.data?.length > 0 ? (
            <div className="overflow-x-auto">
              <p className="text-sm text-gray-500 mb-2">
                Mostrando {maestroData.data.length} de {maestroData.total?.toLocaleString()} registros
              </p>
              <table className="min-w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    {maestroData.columnas?.slice(0, 10).map((col, i) => (
                      <th key={i} className="border border-gray-300 px-2 py-1 text-left font-semibold">
                        {col}
                      </th>
                    ))}
                    {maestroData.columnas?.length > 10 && (
                      <th className="border border-gray-300 px-2 py-1 text-left font-semibold text-gray-400">
                        +{maestroData.columnas.length - 10} más
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {maestroData.data.slice(0, 50).map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      {maestroData.columnas?.slice(0, 10).map((col, j) => (
                        <td key={j} className="border border-gray-300 px-2 py-1 max-w-[150px] truncate">
                          {row[col] !== null && row[col] !== undefined ? String(row[col]) : '-'}
                        </td>
                      ))}
                      {maestroData.columnas?.length > 10 && (
                        <td className="border border-gray-300 px-2 py-1 text-gray-400">...</td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              {maestroData.data.length > 50 && (
                <p className="text-xs text-gray-400 mt-2">Mostrando primeras 50 filas de {maestroData.data.length}</p>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">Sin datos disponibles</div>
          )}
        </div>
      )}
      
      {/* Informe Investigación */}
      <div className="bg-white border-2 border-blue-200 rounded-xl p-6">
        <h3 className="font-bold text-blue-800 mb-3 text-lg">📊 Informe de Investigación (Base para Duplicidad)</h3>
        
        {status?.informe_investigacion?.cache_loaded && status?.informe_investigacion?.cache_registros > 0 && (
          <div className="mb-3 flex items-center justify-between">
            <span className="text-sm text-green-600">
              ✅ {status.informe_investigacion.cache_registros?.toLocaleString()} registros cargados
              {status.informe_investigacion.sincronizado && <span className="ml-2 text-xs text-blue-500">💾 Sincronizado</span>}
            </span>
            <button
              onClick={() => viewMaestroData('informe_investigacion')}
              className={`px-3 py-1 text-xs rounded-lg border ${
                viewingMaestro === 'informe_investigacion' 
                  ? 'bg-blue-100 border-blue-300 text-blue-700' 
                  : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
              }`}
            >
              {viewingMaestro === 'informe_investigacion' ? '📊 Ocultar' : '👁️ Ver datos'}
            </button>
          </div>
        )}
        
        <input
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={(e) => e.target.files[0] && handleInformeUpload(e.target.files[0])}
          className="w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          disabled={uploading.informe}
        />
        {uploading.informe && <p className="text-sm text-gray-500 mt-2">⏳ Cargando...</p>}
      </div>
      
      {/* Excel Maestro para Captura Masiva */}
      <div className="bg-white border-2 border-green-200 rounded-xl p-6">
        <h3 className="font-bold text-green-800 mb-3 text-lg">📊 Excel Maestro (Para Captura Masiva)</h3>
        <p className="text-sm text-gray-600 mb-4">
          Este archivo define los productos disponibles por POS y DÍA para la captura masiva de investigaciones.
        </p>
        
        {excelMaestroStatus?.loaded && (
          <div className="mb-3 p-3 bg-green-50 rounded-lg border border-green-200">
            <p className="text-sm text-green-700">
              ✅ <strong>{excelMaestroStatus.registros?.toLocaleString()}</strong> registros | 
              <strong> {excelMaestroStatus.pos_count}</strong> POS únicos
            </p>
          </div>
        )}
        
        <input
          type="file"
          accept=".xlsx,.xls"
          onChange={(e) => e.target.files[0] && handleExcelMaestroUpload(e.target.files[0])}
          className="w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
          disabled={uploading.excel_maestro}
        />
        {uploading.excel_maestro && <p className="text-sm text-gray-500 mt-2">⏳ Cargando Excel Maestro...</p>}
      </div>
      
      {/* Carga de Investigaciones desde Excel (Captura Masiva) */}
      <div className="bg-white border-2 border-purple-200 rounded-xl p-6">
        <h3 className="font-bold text-purple-800 mb-3 text-lg">📊 Carga de Investigaciones (Excel → Captura Masiva)</h3>
        <p className="text-sm text-gray-600 mb-3">
          Sube investigaciones desde un archivo Excel directamente a la tabla de Captura Masiva.
          La fecha y el usuario se capturan automáticamente.
        </p>
        
        <div className="bg-purple-50 rounded-lg p-3 mb-4 text-xs">
          <p className="font-bold text-purple-800 mb-1">📋 Campos requeridos en el Excel:</p>
          <ul className="grid grid-cols-2 md:grid-cols-3 gap-1 text-purple-700">
            <li>• <code className="bg-white px-1 rounded">idproducto</code></li>
            <li>• <code className="bg-white px-1 rounded">competidor</code></li>
            <li>• <code className="bg-white px-1 rounded">precio_competencia</code></li>
            <li>• <code className="bg-white px-1 rounded">pos</code> (opcional)</li>
            <li>• <code className="bg-white px-1 rounded">items</code> (opcional)</li>
            <li>• <code className="bg-white px-1 rounded">pvp</code> (opcional)</li>
            <li>• <code className="bg-white px-1 rounded">estrategia</code> (opcional)</li>
            <li>• <code className="bg-white px-1 rounded">laboratorio</code> (opcional)</li>
            <li>• <code className="bg-white px-1 rounded">descuento</code> (opcional)</li>
          </ul>
          <p className="text-purple-600 mt-2 italic">* fecha, usuario y mes se capturan automáticamente</p>
        </div>
        
        <input
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={async (e) => {
            if (!e.target.files[0]) return;
            setUploading(prev => ({...prev, investigaciones_excel: true}));
            const formData = new FormData();
            formData.append('file', e.target.files[0]);
            try {
              const token = localStorage.getItem('token');
              const res = await fetch(`${API}/api/investigaciones/upload-excel`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
              });
              const data = await res.json();
              if (data.success) {
                const extraScrape = data.urls_pendientes_scrape > 0
                  ? ` · 📸 capturando ${data.urls_pendientes_scrape} URL(s) en background`
                  : '';
                toast.success(`✅ ${data.registros_cargados} investigaciones cargadas (${data.competidores?.join(', ')})${extraScrape}`);
                fetchResumenCapturas();
              } else {
                toast.error(data.detail || 'Error al cargar');
              }
            } catch (err) {
              toast.error('Error de conexión');
            }
            setUploading(prev => ({...prev, investigaciones_excel: false}));
            e.target.value = '';
          }}
          className="w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
          disabled={uploading.investigaciones_excel}
        />
        {uploading.investigaciones_excel && <p className="text-sm text-purple-500 mt-2">⏳ Cargando investigaciones...</p>}

        {/* Panel: Reprocesar URLs pendientes de scrape */}
        <ScrapePendientesPanel API={API} />
      </div>
      
      {/* Backup y Migración de Datos */}
      <div className="bg-white border-2 border-orange-200 rounded-xl p-6">
        <h3 className="font-bold text-orange-800 mb-3 text-lg">💾 Backup y Migración de Capturas</h3>
        <p className="text-sm text-gray-600 mb-4">
          Exporte los datos de capturas para hacer backup o migrar a producción. 
          Importe datos previamente exportados.
        </p>
        
        {/* Resumen de datos existentes */}
        {resumenCapturas && (
          <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h4 className="font-semibold text-gray-700 mb-3">📊 Datos Actuales en el Sistema</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div className="p-2 bg-blue-50 rounded border border-blue-200">
                <p className="text-blue-600 font-bold text-lg">{resumenCapturas.sistema_nuevo?.total || 0}</p>
                <p className="text-blue-700 text-xs">Capturas Nuevas</p>
              </div>
              <div className="p-2 bg-purple-50 rounded border border-purple-200">
                <p className="text-purple-600 font-bold text-lg">{resumenCapturas.sistema_anterior?.total || 0}</p>
                <p className="text-purple-700 text-xs">Capturas Anteriores</p>
              </div>
              <div className="p-2 bg-green-50 rounded border border-green-200">
                <p className="text-green-600 font-bold text-lg">{resumenCapturas.informe_investigacion?.total || 0}</p>
                <p className="text-green-700 text-xs">Informe Investigación</p>
              </div>
              <div className="p-2 bg-orange-50 rounded border border-orange-200">
                <p className="text-orange-600 font-bold text-lg">{resumenCapturas.total_general || 0}</p>
                <p className="text-orange-700 text-xs">Total General</p>
              </div>
            </div>
          </div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Exportar */}
          <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
            <h4 className="font-semibold text-orange-700 mb-2">📤 Exportar Datos</h4>
            <p className="text-xs text-gray-500 mb-3">Descarga capturas (ambos sistemas) en formato JSON</p>
            <div className="space-y-2">
              <button
                onClick={() => exportCapturas(false)}
                disabled={exportingData}
                className="w-full px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-sm font-medium disabled:opacity-50"
              >
                {exportingData ? '⏳ Exportando...' : '📥 Backup Rápido (Solo Capturas)'}
              </button>
              <button
                onClick={() => exportCapturas(true)}
                disabled={exportingData}
                className="w-full px-4 py-2 bg-orange-400 hover:bg-orange-500 text-white rounded-lg text-xs font-medium disabled:opacity-50"
              >
                📥 Backup Completo (+ Informe 6K registros)
              </button>
            </div>
          </div>
          
          {/* Importar */}
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h4 className="font-semibold text-blue-700 mb-2">📥 Importar Datos</h4>
            <p className="text-xs text-gray-500 mb-3">Restaura capturas desde un archivo JSON</p>
            <input
              type="file"
              accept=".json"
              onChange={(e) => e.target.files[0] && importCapturas(e.target.files[0])}
              className="w-full text-sm file:mr-2 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-blue-100 file:text-blue-700 hover:file:bg-blue-200"
              disabled={uploading.backup}
            />
            {uploading.backup && <p className="text-xs text-gray-500 mt-2">⏳ Importando...</p>}
          </div>
        </div>
      </div>
    </div>
  );
}


// ============================================
// SEGUIMIENTO TAB - Dashboard de Avance de Captura
// ============================================
function SeguimientoTab() {
  const [loading, setLoading] = useState(false);
  const [meses, setMeses] = useState([]);
  const [owners, setOwners] = useState([]);
  const [mesSeleccionado, setMesSeleccionado] = useState('');
  const [ownerSeleccionado, setOwnerSeleccionado] = useState('');
  const [comparativa, setComparativa] = useState(null);
  const [activeView, setActiveView] = useState('resumen');
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadFiltros();
  }, []);

  const loadFiltros = async () => {
    try {
      const token = localStorage.getItem('token');
      const [mesesRes, ownersRes] = await Promise.all([
        fetch(`${API}/api/investigacion/meses`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API}/api/seguimiento/owners`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      if (mesesRes.ok) {
        const data = await mesesRes.json();
        setMeses(data || []);
      }
      if (ownersRes.ok) {
        const data = await ownersRes.json();
        setOwners(data || []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const cargarComparativa = async () => {
    setLoading(true);
    setMessage('');
    try {
      const token = localStorage.getItem('token');
      let url = `${API}/api/seguimiento/comparativa`;
      const params = new URLSearchParams();
      if (mesSeleccionado) params.append('mes', mesSeleccionado);
      if (ownerSeleccionado) params.append('owner', ownerSeleccionado);
      if (params.toString()) url += `?${params.toString()}`;
      
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        setComparativa(data);
        setMessage('✅ Comparativa cargada correctamente');
      } else {
        setMessage(`❌ ${data.detail || 'Error al cargar comparativa'}`);
      }
    } catch (err) {
      setMessage(`❌ Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const exportarExcel = async () => {
    try {
      const token = localStorage.getItem('token');
      let url = `${API}/api/seguimiento/export/excel`;
      const params = new URLSearchParams();
      if (mesSeleccionado) params.append('mes', mesSeleccionado);
      if (ownerSeleccionado) params.append('owner', ownerSeleccionado);
      if (params.toString()) url += `?${params.toString()}`;
      
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const blob = await res.blob();
        const urlBlob = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = urlBlob;
        a.download = `Seguimiento_Captura_${mesSeleccionado || 'TODOS'}_${ownerSeleccionado || 'TODOS'}_${new Date().toISOString().slice(0,10)}.xlsx`;
        a.click();
        setMessage('✅ Excel exportado correctamente');
      } else {
        setMessage('❌ Error al exportar Excel');
      }
    } catch (err) {
      setMessage(`❌ Error: ${err.message}`);
    }
  };

  const getPctColor = (pct) => {
    if (pct >= 80) return 'text-green-600 bg-green-50';
    if (pct >= 50) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-6">
        <h2 className="text-2xl font-bold text-purple-800 mb-2">📊 Dashboard de Seguimiento</h2>
        <p className="text-purple-600">Compare el avance de captura vs el Excel Maestro por POS y DÍA</p>
      </div>

      {/* Mensaje */}
      {message && (
        <div className={`p-4 rounded-lg ${message.includes('✅') ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          {message}
        </div>
      )}

      {/* Filtros y acciones */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[180px]">
            <label className="block text-sm font-bold text-gray-700 mb-2">📅 Filtrar por Mes</label>
            <select
              value={mesSeleccionado}
              onChange={(e) => setMesSeleccionado(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              data-testid="select-mes"
            >
              <option value="">Todos los meses</option>
              {meses.map((mes, idx) => (
                <option key={idx} value={mes}>{mes}</option>
              ))}
            </select>
          </div>
          
          <div className="flex-1 min-w-[180px]">
            <label className="block text-sm font-bold text-gray-700 mb-2">👤 Filtrar por Owner</label>
            <select
              value={ownerSeleccionado}
              onChange={(e) => setOwnerSeleccionado(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              data-testid="select-owner"
            >
              <option value="">Todos los owners</option>
              {owners.map((owner, idx) => (
                <option key={idx} value={owner}>{owner}</option>
              ))}
            </select>
          </div>
          
          <button
            onClick={cargarComparativa}
            disabled={loading}
            className="px-6 py-2 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 disabled:bg-gray-400 transition-colors"
            data-testid="btn-cargar-comparativa"
          >
            {loading ? '⏳ Cargando...' : '🔍 Cargar Comparativa'}
          </button>
          
          {comparativa && (
            <button
              onClick={exportarExcel}
              className="px-6 py-2 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors"
              data-testid="btn-export-seguimiento"
            >
              📥 Exportar Excel
            </button>
          )}
        </div>
      </div>

      {/* Resultados */}
      {comparativa && (
        <>
          {/* Resumen General */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4">📈 Resumen General</h3>
            
            {/* Info de fuentes de datos */}
            <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200 text-sm">
              <span className="font-semibold text-gray-700">Fuentes de datos: </span>
              <span className="text-blue-600">{comparativa.resumen?.investigaciones_sistema_nuevo || 0} capturas nuevas</span>
              <span className="text-gray-400 mx-2">+</span>
              <span className="text-purple-600">{comparativa.resumen?.investigaciones_sistema_anterior || 0} capturas históricas</span>
              <span className="text-gray-400 mx-2">=</span>
              <span className="font-bold text-gray-800">{comparativa.resumen?.total_investigaciones || 0} total</span>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
                <p className="text-xs text-purple-600">Total POS Maestro</p>
                <p className="text-2xl font-bold text-purple-800">{comparativa.resumen?.total_pos_maestro?.toLocaleString()}</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                <p className="text-xs text-blue-600">Productos Maestro</p>
                <p className="text-2xl font-bold text-blue-800">{comparativa.resumen?.total_productos_maestro?.toLocaleString()}</p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <p className="text-xs text-green-600">Capturados</p>
                <p className="text-2xl font-bold text-green-800">{comparativa.resumen?.total_capturados?.toLocaleString()}</p>
              </div>
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-center">
                <p className="text-xs text-orange-600">Pendientes</p>
                <p className="text-2xl font-bold text-orange-800">{comparativa.resumen?.total_pendientes?.toLocaleString()}</p>
              </div>
              <div className={`border rounded-lg p-4 text-center ${getPctColor(comparativa.resumen?.porcentaje_general || 0)}`}>
                <p className="text-xs">% Cumplimiento</p>
                <p className="text-2xl font-bold">{comparativa.resumen?.porcentaje_general?.toFixed(1)}%</p>
              </div>
            </div>

            {/* Progress bar */}
            <div className="w-full bg-gray-200 rounded-full h-4">
              <div 
                className={`h-4 rounded-full ${
                  comparativa.resumen?.porcentaje_general >= 80 ? 'bg-green-500' :
                  comparativa.resumen?.porcentaje_general >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                }`}
                style={{ width: `${Math.min(comparativa.resumen?.porcentaje_general || 0, 100)}%` }}
              ></div>
            </div>
          </div>

          {/* Tabs de detalle */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <div className="flex gap-2 mb-4 border-b pb-2 overflow-x-auto">
              {[
                { id: 'pos', label: '📍 Por POS' },
                { id: 'dia', label: '📅 Por DÍA' },
                { id: 'owner', label: '👤 Por OWNER' },
                { id: 'pos_dia', label: '📍📅 Por POS y DÍA' }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveView(tab.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                    activeView === tab.id ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Por POS */}
            {activeView === 'pos' && comparativa.cumplimiento_por_pos && (
              <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left">POS</th>
                      <th className="px-3 py-2 text-right">Maestro</th>
                      <th className="px-3 py-2 text-right">Capturados</th>
                      <th className="px-3 py-2 text-right">Pendientes</th>
                      <th className="px-3 py-2 text-right">% Cumplimiento</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparativa.cumplimiento_por_pos.map((item, idx) => (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        <td className="px-3 py-2 font-mono font-medium">{item.pos}</td>
                        <td className="px-3 py-2 text-right">{item.productos_maestro}</td>
                        <td className="px-3 py-2 text-right text-green-600">{item.productos_capturados}</td>
                        <td className="px-3 py-2 text-right text-orange-600">{item.pendientes}</td>
                        <td className={`px-3 py-2 text-right font-bold ${getPctColor(item.porcentaje_cumplimiento)}`}>
                          {item.porcentaje_cumplimiento.toFixed(1)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Por DÍA */}
            {activeView === 'dia' && comparativa.cumplimiento_por_dia && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-3 py-2 text-left">DÍA</th>
                      <th className="px-3 py-2 text-right">Items Maestro</th>
                      <th className="px-3 py-2 text-right">Items Capturados</th>
                      <th className="px-3 py-2 text-right">Pendientes</th>
                      <th className="px-3 py-2 text-right">% Cumplimiento</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparativa.cumplimiento_por_dia.map((item, idx) => (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        <td className="px-3 py-2 font-medium">{item.dia}</td>
                        <td className="px-3 py-2 text-right">{item.items_maestro}</td>
                        <td className="px-3 py-2 text-right text-green-600">{item.items_capturados}</td>
                        <td className="px-3 py-2 text-right text-orange-600">{item.pendientes}</td>
                        <td className={`px-3 py-2 text-right font-bold ${getPctColor(item.porcentaje_cumplimiento)}`}>
                          {item.porcentaje_cumplimiento.toFixed(1)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Por OWNER */}
            {activeView === 'owner' && comparativa.cumplimiento_por_owner && (
              <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left">OWNER</th>
                      <th className="px-3 py-2 text-right">Total POS</th>
                      <th className="px-3 py-2 text-right">Items Maestro</th>
                      <th className="px-3 py-2 text-right">Items Capturados</th>
                      <th className="px-3 py-2 text-right">Pendientes</th>
                      <th className="px-3 py-2 text-right">% Cumplimiento</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparativa.cumplimiento_por_owner.map((item, idx) => (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        <td className="px-3 py-2 font-medium">{item.owner}</td>
                        <td className="px-3 py-2 text-right text-blue-600">{item.total_pos}</td>
                        <td className="px-3 py-2 text-right">{item.items_maestro}</td>
                        <td className="px-3 py-2 text-right text-green-600">{item.items_capturados}</td>
                        <td className="px-3 py-2 text-right text-orange-600">{item.pendientes}</td>
                        <td className={`px-3 py-2 text-right font-bold ${getPctColor(item.porcentaje_cumplimiento)}`}>
                          {item.porcentaje_cumplimiento.toFixed(1)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Por POS y DÍA */}
            {activeView === 'pos_dia' && comparativa.cumplimiento_por_pos_dia && (
              <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left">POS</th>
                      <th className="px-3 py-2 text-left">DÍA</th>
                      <th className="px-3 py-2 text-right">Maestro</th>
                      <th className="px-3 py-2 text-right">Capturados</th>
                      <th className="px-3 py-2 text-right">Pendientes</th>
                      <th className="px-3 py-2 text-right">% Cumplimiento</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparativa.cumplimiento_por_pos_dia.map((item, idx) => (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        <td className="px-3 py-2 font-mono">{item.pos}</td>
                        <td className="px-3 py-2">{item.dia}</td>
                        <td className="px-3 py-2 text-right">{item.productos_maestro}</td>
                        <td className="px-3 py-2 text-right text-green-600">{item.productos_capturados}</td>
                        <td className="px-3 py-2 text-right text-orange-600">{item.pendientes}</td>
                        <td className={`px-3 py-2 text-right font-bold ${getPctColor(item.porcentaje_cumplimiento)}`}>
                          {item.porcentaje_cumplimiento.toFixed(1)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}


// ============================================
// PIPELINE TAB
// ============================================
function PipelineTab() {
  const [executing, setExecuting] = useState({});
  const [results, setResults] = useState({});
  const [asyncJob, setAsyncJob] = useState(null);
  const [asyncProgress, setAsyncProgress] = useState(0);
  const [asyncStatus, setAsyncStatus] = useState(null);
  const [maestrosStatus, setMaestrosStatus] = useState(null);
  const [loadingMaestro, setLoadingMaestro] = useState(null); // 'negociacion', 'franquicia', 'comercial' o null
  const [jobProgress, setJobProgress] = useState({}); // {negociacion: 50, ...}
  const pollingRef = React.useRef(null);
  const retryCountRef = React.useRef(0);
  const maxRetries = 3;
  
  // Verificar estado de maestros al montar
  React.useEffect(() => {
    verificarMaestros();
  }, []);
  
  const verificarMaestros = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/pipeline/maestros-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMaestrosStatus(data);
        return data;
      }
    } catch (err) {
      console.error('Error verificando maestros:', err);
    }
    return null;
  };
  
  // Polling de un job específico
  const pollJob = async (jobId, tipo) => {
    const token = localStorage.getItem('token');
    const nombres = { negociacion: 'Negociación', franquicia: 'Franquicia', comercial: 'Guía Comercial' };
    
    let intentos = 0;
    const maxIntentos = 60; // 3 minutos máximo
    
    const poll = async () => {
      intentos++;
      try {
        const res = await fetch(`${API}/api/pipeline/maestro-job/${jobId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (!res.ok) {
          throw new Error('Job no encontrado');
        }
        
        const job = await res.json();
        
        // Actualizar progreso
        setJobProgress(prev => ({ ...prev, [tipo]: job.progress }));
        
        if (job.status === 'completed') {
          toast.success(`✅ ${nombres[tipo]}: ${job.detalle?.registros?.toLocaleString() || ''} registros`, { id: `load-${tipo}` });
          setLoadingMaestro(null);
          setJobProgress(prev => ({ ...prev, [tipo]: null }));
          await verificarMaestros();
          return true;
        } else if (job.status === 'error') {
          toast.error(`❌ Error: ${job.message}`, { id: `load-${tipo}` });
          setLoadingMaestro(null);
          setJobProgress(prev => ({ ...prev, [tipo]: null }));
          return false;
        } else {
          // En progreso
          toast.loading(`${job.message} (${job.progress}%)`, { id: `load-${tipo}` });
          
          if (intentos < maxIntentos) {
            setTimeout(poll, 3000);
          } else {
            toast.error(`Timeout cargando ${nombres[tipo]}`, { id: `load-${tipo}` });
            setLoadingMaestro(null);
            setJobProgress(prev => ({ ...prev, [tipo]: null }));
          }
        }
      } catch (err) {
        console.error('Error polling job:', err);
        if (intentos < maxIntentos) {
          setTimeout(poll, 3000);
        } else {
          setLoadingMaestro(null);
          setJobProgress(prev => ({ ...prev, [tipo]: null }));
        }
      }
    };
    
    setTimeout(poll, 2000);
  };
  
  // Cargar UN maestro específico
  const cargarMaestro = async (tipo) => {
    setLoadingMaestro(tipo);
    const nombres = { negociacion: 'Negociación', franquicia: 'Franquicia', comercial: 'Guía Comercial' };
    toast.loading(`Iniciando carga de ${nombres[tipo]}...`, { id: `load-${tipo}` });
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/pipeline/cargar-maestro/${tipo}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        if (data.job_id) {
          // Carga iniciada en background - hacer polling del job
          toast.loading(`Cargando ${nombres[tipo]}...`, { id: `load-${tipo}` });
          pollJob(data.job_id, tipo);
        } else {
          // Cargado inmediatamente desde archivo
          toast.success(`✅ ${nombres[tipo]}: ${data.registros?.toLocaleString()} registros`, { id: `load-${tipo}` });
          setLoadingMaestro(null);
          await verificarMaestros();
        }
      } else {
        toast.error(data.detail || data.error || `Error cargando ${nombres[tipo]}`, { id: `load-${tipo}` });
        setLoadingMaestro(null);
      }
    } catch (err) {
      toast.error(`Error de conexión`, { id: `load-${tipo}` });
      setLoadingMaestro(null);
    }
  };
  
  // Cargar todos los maestros en secuencia
  const cargarTodosMaestros = async () => {
    const orden = ['negociacion', 'franquicia', 'comercial'];
    
    for (const tipo of orden) {
      const status = await verificarMaestros();
      if (status?.maestros?.[tipo]?.listo) continue;
      
      await new Promise((resolve) => {
        cargarMaestro(tipo);
        // Esperar hasta que termine este maestro
        const checkInterval = setInterval(async () => {
          const currentStatus = await verificarMaestros();
          if (currentStatus?.maestros?.[tipo]?.listo) {
            clearInterval(checkInterval);
            setTimeout(resolve, 1000);
          }
        }, 5000);
        
        // Timeout de seguridad
        setTimeout(() => {
          clearInterval(checkInterval);
          resolve();
        }, 180000); // 3 minutos max por maestro
      });
    }
    
    await verificarMaestros();
  };
  
  // Función para limpiar estado del polling
  const cleanupPolling = React.useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    retryCountRef.current = 0;
  }, []);
  
  // Cleanup al desmontar
  React.useEffect(() => {
    return () => cleanupPolling();
  }, [cleanupPolling]);
  
  const checkAsyncStatus = async (jobId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/pipeline/status/${jobId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Si el job no existe (404), detener polling
      if (res.status === 404) {
        console.warn('Job no encontrado, deteniendo polling');
        cleanupPolling();
        setExecuting(prev => ({ ...prev, todo: false }));
        setAsyncJob(null);
        setAsyncProgress(0);
        toast.warning('El job de pipeline expiró. Ejecute de nuevo.');
        return;
      }
      
      // Reset retry counter on success
      retryCountRef.current = 0;
      
      const data = await res.json();
      
      if (data.success) {
        setAsyncProgress(data.progress);
        setAsyncStatus(data);
        
        // Actualizar resultados parciales
        if (data.results) {
          setResults(prev => ({ ...prev, ...data.results }));
        }
        
        // Si completó o error, detener polling
        if (data.status === 'completed' || data.status === 'error') {
          cleanupPolling();
          setExecuting(prev => ({ ...prev, todo: false }));
          
          if (data.status === 'completed') {
            toast.success('✅ Pipeline completo ejecutado');
          } else if (data.error) {
            toast.error(`Error: ${data.error}`);
          }
        }
      } else {
        // Respuesta no exitosa, detener polling
        cleanupPolling();
        setExecuting(prev => ({ ...prev, todo: false }));
      }
    } catch (err) {
      console.error('Error polling status:', err);
      retryCountRef.current++;
      
      // Si superamos los reintentos, detener
      if (retryCountRef.current >= maxRetries) {
        console.warn(`Máximo de reintentos (${maxRetries}) alcanzado, deteniendo polling`);
        cleanupPolling();
        setExecuting(prev => ({ ...prev, todo: false }));
        toast.error('Error de conexión. Verifique el estado del pipeline manualmente.');
      }
    }
  };
  
  const ejecutarPaso = async (paso) => {
    setExecuting(prev => ({ ...prev, [paso]: true }));
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/pipeline/${paso}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        toast.success(`✅ ${paso.toUpperCase()} completado`);
        setResults(prev => ({ ...prev, [paso]: data }));
      } else {
        toast.error(data.detail || `Error en ${paso}`);
      }
    } catch (err) {
      toast.error('Error de conexión');
    } finally {
      setExecuting(prev => ({ ...prev, [paso]: false }));
    }
  };
  
  const ejecutarTodoAsync = async () => {
    setExecuting(prev => ({ ...prev, todo: true }));
    setAsyncProgress(0);
    setAsyncStatus(null);
    setResults({});
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/pipeline/ejecutar-async`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await res.json();
      
      if (data.success && data.job_id) {
        setAsyncJob(data.job_id);
        toast.info('🚀 Pipeline iniciado en segundo plano');
        
        // Iniciar polling cada 2 segundos
        pollingRef.current = setInterval(() => {
          checkAsyncStatus(data.job_id);
        }, 2000);
        
      } else if (data.existing_job_id) {
        // Ya hay un job corriendo
        setAsyncJob(data.existing_job_id);
        setAsyncProgress(data.progress || 0);
        toast.warning('Ya hay un pipeline en ejecución');
        
        // Iniciar polling para el job existente
        pollingRef.current = setInterval(() => {
          checkAsyncStatus(data.existing_job_id);
        }, 2000);
        
      } else {
        toast.error(data.detail || 'Error iniciando pipeline');
        setExecuting(prev => ({ ...prev, todo: false }));
      }
    } catch (err) {
      toast.error('Error de conexión');
      setExecuting(prev => ({ ...prev, todo: false }));
    }
  };
  
  const pasos = [
    { id: 'duplicidad', label: 'PASO 2: Duplicidad', desc: 'Expandir registros a todas las tablas por competidor' },
    { id: 'paso3', label: 'PASO 3: Mínimo + Fórmulas', desc: 'Calcular mínimo competencia, cruces y fórmulas' },
    { id: 'paso4', label: 'PASO 4: 60% Competitivo', desc: 'Filtrar productos ≥60% competitivo' },
    { id: 'paso5', label: 'PASO 5: 60% No Competitivo', desc: 'Filtrar productos ≥60% no competitivo' }
  ];
  
  const getStepLabel = (step) => {
    const labels = {
      'initializing': 'Inicializando...',
      'duplicidad': 'Paso 2: Duplicidad',
      'paso3': 'Paso 3: Fórmulas',
      'paso4': 'Paso 4: 60% Competitivo',
      'paso5': 'Paso 5: 60% No Competitivo',
      'done': 'Completado'
    };
    return labels[step] || step;
  };
  
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-6">
        <h2 className="text-2xl font-bold text-purple-800 mb-2">🔄 Pipeline de Análisis</h2>
        <p className="text-purple-600">Ejecute los pasos del análisis de competitividad</p>
      </div>
      
      {/* Sección Cargar Maestros - CON JOBS */}
      <div className={`rounded-xl p-6 border-2 ${maestrosStatus?.listos_para_pipeline ? 'bg-green-50 border-green-300' : 'bg-yellow-50 border-yellow-300'}`}>
        <div className="mb-4">
          <h3 className={`text-lg font-bold ${maestrosStatus?.listos_para_pipeline ? 'text-green-800' : 'text-yellow-800'}`}>
            {maestrosStatus?.listos_para_pipeline ? '✅ Maestros Cargados' : '⚠️ Cargar Maestros'}
          </h3>
          <p className={`text-sm ${maestrosStatus?.listos_para_pipeline ? 'text-green-600' : 'text-yellow-600'}`}>
            {maestrosStatus?.listos_para_pipeline 
              ? 'Todos los maestros están listos para ejecutar el Pipeline'
              : 'Cargue cada maestro. El proceso corre en segundo plano.'}
          </p>
        </div>
        
        {/* Botones individuales para cada maestro */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          {[
            { key: 'negociacion', label: 'Negociación', icon: '📋' },
            { key: 'franquicia', label: 'Franquicia', icon: '💰' },
            { key: 'comercial', label: 'Guía Comercial', icon: '📊' }
          ].map(m => {
            const status = maestrosStatus?.maestros?.[m.key];
            const isLoading = loadingMaestro === m.key;
            const progress = jobProgress[m.key];
            const isListo = status?.listo;
            
            return (
              <div key={m.key} className={`p-3 rounded-lg border ${
                isLoading ? 'bg-blue-50 border-blue-300' :
                isListo ? 'bg-green-100 border-green-300' : 'bg-white border-gray-300'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">{m.icon} {m.label}</span>
                  {isLoading && progress && <span className="text-blue-600 text-sm">{progress}%</span>}
                  {isListo && !isLoading && <span className="text-green-600 text-sm">✓ {status.registros?.toLocaleString()}</span>}
                </div>
                {isListo && !isLoading && (
                  <div className="text-[10px] text-gray-500 mb-1">
                    Fuente: {status.en_cache ? '🟢 Memoria' : status.en_archivo ? '🟡 Archivo' : status.en_mongo ? '🔵 MongoDB' : '—'}
                  </div>
                )}
                
                {/* Barra de progreso */}
                {isLoading && progress && (
                  <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                )}
                
                <button
                  onClick={() => cargarMaestro(m.key)}
                  disabled={isLoading || loadingMaestro !== null}
                  data-testid={`btn-cargar-${m.key}`}
                  className={`w-full py-2 px-3 rounded text-sm font-medium transition-colors ${
                    isLoading ? 'bg-blue-500 text-white' :
                    isListo ? 'bg-green-500 text-white hover:bg-green-600' 
                           : 'bg-yellow-500 text-white hover:bg-yellow-600'
                  } disabled:bg-gray-400 disabled:cursor-not-allowed`}
                >
                  {isLoading ? '⏳ Cargando...' : isListo ? '🔄 Recargar' : '📥 Cargar'}
                </button>
              </div>
            );
          })}
        </div>
        
        {/* Botón para cargar todos en secuencia */}
        {!maestrosStatus?.listos_para_pipeline && (
          <button
            onClick={cargarTodosMaestros}
            disabled={loadingMaestro !== null}
            data-testid="btn-cargar-todos-maestros"
            className="w-full py-3 bg-purple-600 text-white font-bold rounded-lg hover:bg-purple-700 disabled:bg-gray-400 transition-colors"
          >
            {loadingMaestro ? `⏳ Cargando...` : '📥 Cargar Todos (Secuencial)'}
          </button>
        )}
      </div>
      
      {/* Botón ejecutar todo (Async) */}
      <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl p-6 text-white">
        <h3 className="text-xl font-bold mb-2">🚀 Ejecutar Pipeline Completo</h3>
        <p className="text-green-100 mb-4">Ejecuta todos los pasos en segundo plano (no bloquea la interfaz)</p>
        
        {/* Barra de progreso */}
        {executing.todo && (
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-1">
              <span>{asyncStatus ? getStepLabel(asyncStatus.current_step) : 'Iniciando...'}</span>
              <span>{asyncProgress}%</span>
            </div>
            <div className="w-full bg-green-300 rounded-full h-3">
              <div 
                className="bg-white h-3 rounded-full transition-all duration-500" 
                style={{ width: `${asyncProgress}%` }}
              />
            </div>
          </div>
        )}
        
        <button
          onClick={ejecutarTodoAsync}
          disabled={executing.todo}
          className="px-6 py-3 bg-white text-green-700 font-bold rounded-lg hover:bg-green-50 disabled:bg-gray-300 disabled:text-gray-500 transition-colors"
        >
          {executing.todo ? `⏳ Ejecutando... ${asyncProgress}%` : '▶️ Ejecutar Todo (Async)'}
        </button>
        
        {asyncJob && !executing.todo && asyncStatus?.status === 'completed' && (
          <p className="mt-2 text-sm text-green-200">✅ Job {asyncJob} completado</p>
        )}
      </div>
      
      {/* Resumen de resultados async */}
      {asyncStatus?.status === 'completed' && asyncStatus.results && (
        <div className="bg-white border border-green-200 rounded-xl p-4">
          <h4 className="font-bold text-green-800 mb-3">📊 Resumen del Pipeline</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(asyncStatus.results).map(([paso, data]) => (
              <div key={paso} className={`p-3 rounded-lg ${data.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <p className="text-xs font-bold uppercase text-gray-600">{paso}</p>
                {data.success ? (
                  <>
                    <p className="text-lg font-bold text-green-700">{data.registros?.toLocaleString() || '✓'}</p>
                    {data.competitivos !== undefined && (
                      <p className="text-xs text-gray-500">
                        Comp: {data.competitivos?.toLocaleString()} | No: {data.no_competitivos?.toLocaleString()}
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-red-600">{data.error || 'Error'}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Pasos individuales */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {pasos.map(paso => (
          <div key={paso.id} className="bg-white border border-gray-200 rounded-xl p-4">
            <h3 className="font-bold text-gray-800 mb-1">{paso.label}</h3>
            <p className="text-sm text-gray-500 mb-3">{paso.desc}</p>
            
            {(results[paso.id]?.estadisticas || results[paso.id]?.success) && (
              <div className="mb-3 p-3 bg-green-50 rounded-lg text-sm">
                <p className="text-green-800 font-medium">✅ Completado</p>
                <p className="text-green-600">
                  Registros: {results[paso.id]?.estadisticas?.registros?.toLocaleString() || 
                             results[paso.id]?.estadisticas?.registros_duplicidad?.toLocaleString() ||
                             results[paso.id]?.registros?.toLocaleString() || '-'}
                </p>
              </div>
            )}
            
            <button
              onClick={() => ejecutarPaso(paso.id)}
              disabled={executing[paso.id] || executing.todo}
              className="w-full py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 transition-colors"
            >
              {executing[paso.id] ? '⏳ Ejecutando...' : '▶️ Ejecutar'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}


// ============================================
// DESCARGAS TAB
// ============================================
function DescargasTab() {
  const [downloading, setDownloading] = useState({});
  
  const descargar = async (tipo) => {
    setDownloading(prev => ({ ...prev, [tipo]: true }));
    toast.loading(`Descargando ${tipo}...`, { id: `download-${tipo}` });
    
    try {
      const token = localStorage.getItem('token');
      
      // Intentar primero con streaming (para archivos grandes)
      let useStreaming = false;
      let res;
      
      // Primera vez: intentar con Excel normal
      res = await fetch(`${API}/api/pipeline/download/${tipo}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Si hay error de gateway (520, 502, etc.), usar streaming
      const gatewayErrors = [502, 503, 504, 520, 521, 522, 524];
      if (gatewayErrors.includes(res.status)) {
        toast.loading(`Archivo grande, usando formato CSV...`, { id: `download-${tipo}` });
        useStreaming = true;
        res = await fetch(`${API}/api/pipeline/export-stream/${tipo}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const extension = useStreaming ? 'csv' : 'xlsx';
        a.download = `${tipo.toUpperCase()}_${new Date().toISOString().slice(0,10)}.${extension}`;
        a.click();
        toast.success(`✅ ${tipo} descargado`, { id: `download-${tipo}` });
      } else {
        const data = await res.json().catch(() => ({ detail: `Error ${res.status}` }));
        toast.error(data.detail || 'Error al descargar', { id: `download-${tipo}` });
      }
    } catch (err) {
      toast.error('Error de conexión', { id: `download-${tipo}` });
    } finally {
      setDownloading(prev => ({ ...prev, [tipo]: false }));
    }
  };
  
  // Descargar con streaming directamente (para archivos muy grandes)
  const descargarStreaming = async (tipo) => {
    setDownloading(prev => ({ ...prev, [`${tipo}_stream`]: true }));
    toast.loading(`Descargando ${tipo} (streaming)...`, { id: `download-stream-${tipo}` });
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/pipeline/export-stream/${tipo}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${tipo.toUpperCase()}_${new Date().toISOString().slice(0,10)}.csv`;
        a.click();
        toast.success(`✅ ${tipo} descargado (CSV)`, { id: `download-stream-${tipo}` });
      } else {
        const data = await res.json().catch(() => ({ detail: `Error ${res.status}` }));
        toast.error(data.detail || 'Error al descargar', { id: `download-stream-${tipo}` });
      }
    } catch (err) {
      toast.error('Error de conexión', { id: `download-stream-${tipo}` });
    } finally {
      setDownloading(prev => ({ ...prev, [`${tipo}_stream`]: false }));
    }
  };

  const descargarCapturaMasiva = async () => {
    setDownloading(prev => ({ ...prev, captura: true }));
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/captura-masiva/export-excel`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Captura_Masiva_${new Date().toISOString().slice(0,10)}.xlsx`;
        a.click();
        toast.success('✅ Captura Masiva descargado');
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Error al descargar');
      }
    } catch (err) {
      toast.error('Error de conexión');
    } finally {
      setDownloading(prev => ({ ...prev, captura: false }));
    }
  };

  const descargarLaboratorio = async () => {
    setDownloading(prev => ({ ...prev, laboratorio: true }));
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/laboratorio-utilidad/export-excel`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Laboratorio_Perdida_${new Date().toISOString().slice(0,10)}.xlsx`;
        a.click();
        toast.success('✅ Laboratorio descargado');
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Error al descargar');
      }
    } catch (err) {
      toast.error('Error de conexión');
    } finally {
      setDownloading(prev => ({ ...prev, laboratorio: false }));
    }
  };
  
  const archivosPipeline = [
    { tipo: 'duplicidad', label: 'Duplicidad.xlsx', desc: 'Registros expandidos por competidor y tabla', icon: '📋' },
    { tipo: 'paso3', label: 'Paso3.xlsx', desc: 'Mínimo competencia + cruces + fórmulas', icon: '📊' },
    { tipo: 'paso4', label: 'Paso4.xlsx', desc: 'Productos ≥60% competitivo', icon: '✅' },
    { tipo: 'paso5', label: 'Paso5.xlsx', desc: 'Productos ≥60% no competitivo', icon: '❌' }
  ];
  
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-xl p-6">
        <h2 className="text-2xl font-bold text-orange-800 mb-2">📥 Descargas</h2>
        <p className="text-orange-600">Descargue los resultados del análisis en Excel</p>
      </div>

      {/* Captura Masiva y Laboratorio */}
      <div className="bg-white border-2 border-indigo-200 rounded-xl p-6">
        <h3 className="text-lg font-bold text-indigo-800 mb-4">📝 Informes de Captura y Análisis</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 flex items-center justify-between">
            <div>
              <h4 className="font-bold text-indigo-800">📝 Captura Masiva</h4>
              <p className="text-sm text-indigo-600">Registros de precios capturados manualmente</p>
            </div>
            <button
              onClick={descargarCapturaMasiva}
              disabled={downloading.captura}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 transition-colors"
              data-testid="btn-download-captura"
            >
              {downloading.captura ? '⏳' : '📥 Descargar'}
            </button>
          </div>
          
          <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 flex items-center justify-between">
            <div>
              <h4 className="font-bold text-teal-800">🔬 Laboratorio Pérdida</h4>
              <p className="text-sm text-teal-600">Análisis de pérdida de utilidad</p>
            </div>
            <button
              onClick={descargarLaboratorio}
              disabled={downloading.laboratorio}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:bg-gray-400 transition-colors"
              data-testid="btn-download-laboratorio"
            >
              {downloading.laboratorio ? '⏳' : '📥 Descargar'}
            </button>
          </div>
        </div>
      </div>

      {/* Pipeline Results */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h3 className="text-lg font-bold text-gray-800 mb-2">🔄 Resultados del Pipeline</h3>
        <p className="text-sm text-gray-500 mb-4">
          📌 Para archivos grandes (+30k registros), use "CSV" para evitar timeout
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {archivosPipeline.map(archivo => (
            <div key={archivo.tipo} className="bg-gray-50 border border-gray-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h4 className="font-bold text-gray-800">{archivo.icon} {archivo.label}</h4>
                  <p className="text-sm text-gray-500">{archivo.desc}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => descargar(archivo.tipo)}
                  disabled={downloading[archivo.tipo]}
                  className="flex-1 px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:bg-gray-400 transition-colors text-sm"
                  data-testid={`btn-download-${archivo.tipo}`}
                >
                  {downloading[archivo.tipo] ? '⏳' : '📥 Excel'}
                </button>
                <button
                  onClick={() => descargarStreaming(archivo.tipo)}
                  disabled={downloading[`${archivo.tipo}_stream`]}
                  className="flex-1 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 transition-colors text-sm"
                  data-testid={`btn-stream-${archivo.tipo}`}
                  title="Recomendado para archivos grandes"
                >
                  {downloading[`${archivo.tipo}_stream`] ? '⏳' : '📥 CSV (Grande)'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


// ============================================
// CAPTURA MASIVA TAB
// ============================================
function CapturaMasivaTab({ userRole = 'user' }) {
  const [posOptions, setPosOptions] = useState([]);
  const [selectedPos, setSelectedPos] = useState('');
  const [diasOptions, setDiasOptions] = useState([]);
  const [selectedDia, setSelectedDia] = useState('');
  const [productos, setProductos] = useState([]);
  const [estrategias, setEstrategias] = useState([]);
  const [competidores, setCompetidores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPos, setLoadingPos] = useState(false);
  const [loadingDias, setLoadingDias] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [captureResult, setCaptureResult] = useState(null);
  const [mesesDisponibles, setMesesDisponibles] = useState([]);
  const [mesExport, setMesExport] = useState('');
  const [exportando, setExportando] = useState(false);  // NUEVO: Estado de exportación
  const [captureData, setCaptureData] = useState({
    estrategia: '',
    competidor: '',
    observaciones: '',
    productos_precios: {}
  });
  
  // Estado para archivos adjuntos
  const [archivosAdjuntos, setArchivosAdjuntos] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  
  // Estado para sub-tabs
  const [activeSubTab, setActiveSubTab] = useState('captura');
  
  // Determinar si el usuario puede exportar (no investigador ni capturador)
  const canExport = !['investigador', 'capturador'].includes(userRole);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    const token = localStorage.getItem('token');
    try {
      // Cargar POS, estrategias, competidores y meses en paralelo
      const [posRes, estRes, compRes, mesesRes] = await Promise.all([
        fetch(`${API}/api/pos`, { headers: { 'Authorization': `Bearer ${token}` }}),
        fetch(`${API}/api/estrategias`, { headers: { 'Authorization': `Bearer ${token}` }}),
        fetch(`${API}/api/competidores`, { headers: { 'Authorization': `Bearer ${token}` }}),
        fetch(`${API}/api/investigacion/meses`, { headers: { 'Authorization': `Bearer ${token}` }})
      ]);
      
      if (posRes.ok) {
        const posList = await posRes.json();
        setPosOptions(posList || []);
      }
      if (estRes.ok) {
        const estList = await estRes.json();
        setEstrategias(estList || []);
      }
      if (compRes.ok) {
        const compList = await compRes.json();
        setCompetidores(compList || []);
      }
      if (mesesRes.ok) {
        const mesesList = await mesesRes.json();
        setMesesDisponibles(mesesList || []);
      }
    } catch (err) {
      console.error('Error cargando datos iniciales:', err);
    }
  };

  // Función para subir archivo
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Validar tipo
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      setMessage('❌ Tipo de archivo no permitido. Use imágenes (JPG, PNG, GIF, WEBP) o PDF');
      return;
    }
    
    // Validar tamaño (10MB)
    if (file.size > 10 * 1024 * 1024) {
      setMessage('❌ Archivo muy grande. Máximo 10MB');
      return;
    }
    
    setUploadingFile(true);
    setMessage('');
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await fetch(`${API}/api/uploads/file`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        setArchivosAdjuntos(prev => [...prev, {
          filename: data.filename,
          original_name: data.original_name,
          url: data.url,
          is_image: data.is_image,
          is_pdf: data.is_pdf
        }]);
        setMessage(`✅ Archivo "${data.original_name}" subido correctamente`);
      } else {
        setMessage(`❌ ${data.detail || 'Error al subir archivo'}`);
      }
    } catch (err) {
      setMessage(`❌ Error: ${err.message}`);
    } finally {
      setUploadingFile(false);
      // Limpiar input
      event.target.value = '';
    }
  };
  
  // Función para eliminar archivo de la lista
  const removeFile = (filename) => {
    setArchivosAdjuntos(prev => prev.filter(f => f.filename !== filename));
  };

  const handlePosChange = async (pos) => {
    setSelectedPos(pos);
    setSelectedDia('');
    setDiasOptions([]);
    setProductos([]);
    setCaptureData({...captureData, productos_precios: {}, estrategia: ''});
    
    if (!pos) return;
    
    setLoadingDias(true);
    setMessage('');
    
    try {
      const token = localStorage.getItem('token');
      
      // Cargar días disponibles para este POS
      const diasRes = await fetch(`${API}/api/dias-por-pos/${encodeURIComponent(pos)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (diasRes.ok) {
        const dias = await diasRes.json();
        setDiasOptions(dias || []);
        
        if (dias.length === 0) {
          setMessage(`⚠️ No hay días configurados para ${pos}`);
        } else {
          setMessage(`✅ ${dias.length} días disponibles para ${pos}. Seleccione un día.`);
        }
      }
    } catch (err) {
      setMessage(`❌ Error: ${err.message}`);
    } finally {
      setLoadingDias(false);
    }
  };

  const handleDiaChange = async (dia) => {
    setSelectedDia(dia);
    setProductos([]);
    setCaptureData({...captureData, productos_precios: {}, estrategia: ''});
    
    if (!selectedPos || !dia) return;
    
    setLoadingPos(true);
    setMessage('');
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/productos-por-pos/${encodeURIComponent(selectedPos)}?dia=${encodeURIComponent(dia)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        // El backend devuelve {productos: [...], total: X, estrategia_auto: "...", dia_filtrado: "..."}
        const prods = data.productos || [];
        const total = data.total || prods.length;
        
        setProductos(prods);
        
        // Auto-seleccionar estrategia si hay una común
        if (data.estrategia_auto) {
          setCaptureData(prev => ({...prev, estrategia: data.estrategia_auto}));
        } else if (prods.length > 0 && prods[0].ESTRATEGIA) {
          setCaptureData(prev => ({...prev, estrategia: prods[0].ESTRATEGIA}));
        }
        
        setMessage(`✅ ${total} productos cargados para ${selectedPos} - ${dia}`);
      } else {
        setMessage('❌ Error al cargar productos');
      }
    } catch (err) {
      setMessage(`❌ Error: ${err.message}`);
    } finally {
      setLoadingPos(false);
    }
  };

  const updateProductPrice = (idProducto, field, value) => {
    setCaptureData(prev => ({
      ...prev,
      productos_precios: {
        ...prev.productos_precios,
        [idProducto]: {
          ...prev.productos_precios[idProducto],
          [field]: value
        }
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar que hay al menos un producto con precio
    const productosConPrecio = Object.entries(captureData.productos_precios)
      .filter(([_, data]) => data.precio_competencia && parseFloat(data.precio_competencia) > 0);
    
    if (productosConPrecio.length === 0) {
      setMessage('⚠️ Debe ingresar al menos un precio de competencia');
      return;
    }
    
    if (!captureData.competidor) {
      setMessage('⚠️ Debe seleccionar un competidor');
      return;
    }
    
    // ============================================
    // VALIDACIÓN DE PRECIOS: Negativos y Descuento > 70%
    // ============================================
    const erroresPrecios = [];
    
    productosConPrecio.forEach(([idProd, priceData]) => {
      const precio = parseFloat(priceData.precio_competencia);
      const prod = productos.find(p => p.IDPRODUCTO === idProd);
      const pvp = prod?.PVP || 0;
      
      // Validar precio negativo
      if (precio < 0) {
        erroresPrecios.push({
          idProducto: idProd,
          producto: prod?.ITEMS || idProd,
          tipo: 'negativo',
          mensaje: `Precio negativo ($${precio.toFixed(2)})`
        });
      }
      
      // Validar descuento negativo (precio mayor que PVP)
      if (pvp > 0 && precio > pvp) {
        const descuentoNeg = ((pvp - precio) / pvp) * 100;
        erroresPrecios.push({
          idProducto: idProd,
          producto: prod?.ITEMS || idProd,
          tipo: 'descuento_negativo',
          mensaje: `Descuento negativo (${descuentoNeg.toFixed(1)}%) - Precio $${precio.toFixed(2)} mayor que PVP $${pvp.toFixed(2)}`
        });
      }
      
      // Validar descuento mayor al 70%
      if (pvp > 0 && precio >= 0 && precio <= pvp) {
        const descuento = ((pvp - precio) / pvp) * 100;
        if (descuento > 70) {
          erroresPrecios.push({
            idProducto: idProd,
            producto: prod?.ITEMS || idProd,
            tipo: 'descuento_excesivo',
            mensaje: `Descuento de ${descuento.toFixed(1)}% excede el máximo de 70% (PVP: $${pvp.toFixed(2)}, Precio: $${precio.toFixed(2)})`
          });
        }
      }
    });
    
    if (erroresPrecios.length > 0) {
      const mensajeError = `🚫 NO SE PUEDE GUARDAR - ${erroresPrecios.length} error(es) de precio:\n\n` +
        erroresPrecios.map((err, idx) => 
          `${idx + 1}. ${err.producto.substring(0, 30)}...\n   ❌ ${err.mensaje}`
        ).join('\n\n');
      
      setMessage(mensajeError);
      return;
    }
    
    setSubmitting(true);
    setMessage('');
    
    try {
      const token = localStorage.getItem('token');
      
      // Agregar info del producto al precio
      const productosConInfo = {};
      productosConPrecio.forEach(([idProd, priceData]) => {
        const prod = productos.find(p => p.IDPRODUCTO === idProd);
        productosConInfo[idProd] = {
          ...priceData,
          items: prod?.ITEMS || '',
          pvp: prod?.PVP || 0,
          laboratorio: prod?.LABORATORIO || ''
        };
      });
      
      const payload = {
        pos: selectedPos,
        competidor: captureData.competidor,
        estrategia: captureData.estrategia,
        observaciones: captureData.observaciones,
        productos_precios: productosConInfo,
        archivos_adjuntos: archivosAdjuntos.map(f => ({
          filename: f.filename,
          original_name: f.original_name,
          url: f.url,
          is_image: f.is_image,
          is_pdf: f.is_pdf
        }))
      };
      
      const res = await fetch(`${API}/api/investigacion/massive-create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        // Guardar resultado para mostrar en el modal
        setCaptureResult({
          productosGuardados: data.total_creados || productosConPrecio.length,
          totalProcesados: productosConPrecio.length,
          pos: selectedPos,
          dia: selectedDia,
          estrategia: captureData.estrategia,
          competidor: captureData.competidor,
          archivosAdjuntos: archivosAdjuntos.length,
          fecha: new Date().toLocaleDateString('es-ES', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          }),
          hora: new Date().toLocaleTimeString('es-ES')
        });
        setShowSuccessModal(true);
        
        // Limpiar archivos adjuntos
        setArchivosAdjuntos([]);
        
        // Limpiar formulario
        setCaptureData({
          estrategia: '',
          competidor: '',
          observaciones: '',
          productos_precios: {}
        });
        setSelectedPos('');
        setSelectedDia('');
        setDiasOptions([]);
        setProductos([]);
        setMessage('');
      } else {
        setMessage(`❌ ${data.detail || 'Error al guardar'}`);
      }
    } catch (err) {
      setMessage(`❌ Error: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const exportExcel = async () => {
    setExportando(true);
    setMessage('⏳ Descargando... Esto puede tardar varios minutos para archivos grandes.');
    toast.loading('Generando archivo...', { id: 'export-excel' });
    
    const maxRetries = 2;
    const baseDelay = 2000;
    let lastError = null;
    let useStreaming = false;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const token = localStorage.getItem('token');
        
        // Si ya falló con Excel, usar streaming CSV
        let url = useStreaming 
          ? `${API}/api/investigacion/export/excel-stream`
          : `${API}/api/investigacion/export/excel`;
        
        if (mesExport) {
          url += `?mes=${encodeURIComponent(mesExport)}`;
        }
        
        // Timeout de 5 minutos para exportaciones grandes
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 300000);
        
        const acceptType = useStreaming 
          ? 'text/csv, application/json' 
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/json';
        
        const res = await fetch(url, {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Accept': acceptType
          },
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        const contentType = res.headers.get('content-type') || '';
        
        // Verificar si es error de gateway (520, 502, etc.)
        const gatewayErrors = [502, 503, 504, 520, 521, 522, 524];
        if (gatewayErrors.includes(res.status)) {
          // Si es primer intento con Excel, cambiar a streaming CSV
          if (!useStreaming && attempt === 0) {
            console.warn(`[Export] Error ${res.status} con Excel, cambiando a streaming CSV...`);
            toast.loading('Archivo grande detectado, usando formato CSV...', { id: 'export-excel' });
            useStreaming = true;
            await new Promise(r => setTimeout(r, 1000));
            continue;
          }
          
          // Si ya estamos en streaming y falla, reintentar
          if (attempt < maxRetries) {
            const delay = baseDelay * Math.pow(2, attempt) + Math.random() * 1000;
            console.warn(`[Export] Retry ${attempt + 1}/${maxRetries} after ${res.status}`);
            toast.loading(`Reintentando... (${attempt + 1}/${maxRetries})`, { id: 'export-excel' });
            await new Promise(r => setTimeout(r, delay));
            continue;
          }
          
          // Todos los reintentos fallaron
          setMessage(`❌ El servidor está ocupado (${res.status}). Intente con un filtro de mes específico.`);
          toast.error('Servidor ocupado. Use filtro de mes.', { id: 'export-excel' });
          return;
        }
        
        // Verificar respuesta exitosa
        if (!res.ok) {
          if (contentType.includes('application/json')) {
            const data = await res.json().catch(() => ({}));
            setMessage(`❌ ${data.detail || 'Error al exportar'}`);
            toast.error(data.detail || 'Error al exportar', { id: 'export-excel' });
          } else {
            setMessage(`❌ Error HTTP ${res.status}`);
            toast.error(`Error HTTP ${res.status}`, { id: 'export-excel' });
          }
          return;
        }
        
        // Descargar el archivo
        const blob = await res.blob();
        
        if (blob.size === 0) {
          setMessage('❌ El archivo descargado está vacío');
          toast.error('Archivo vacío', { id: 'export-excel' });
          return;
        }
        
        // Determinar extensión según el tipo
        const fileExtension = useStreaming || contentType.includes('csv') ? 'csv' : 'xlsx';
        
        // Crear URL y descargar
        const urlObj = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = urlObj;
        a.download = `Informe_Investigacion_${mesExport || 'TODOS'}_${new Date().toISOString().slice(0,10)}.${fileExtension}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(urlObj);
        
        const sizeKB = (blob.size / 1024).toFixed(1);
        const formatMsg = fileExtension === 'csv' ? ' (CSV para Excel)' : '';
        setMessage(`✅ Archivo exportado correctamente${formatMsg} (${sizeKB} KB)`);
        toast.success(`Archivo descargado (${sizeKB} KB)`, { id: 'export-excel' });
        return;
        
      } catch (err) {
        lastError = err;
        
        if (err.name === 'AbortError') {
          // Si timeout con Excel, intentar con streaming
          if (!useStreaming) {
            console.warn('[Export] Timeout con Excel, cambiando a streaming CSV...');
            toast.loading('Timeout detectado, usando formato CSV...', { id: 'export-excel' });
            useStreaming = true;
            continue;
          }
          setMessage('❌ Tiempo de espera agotado. Intente con un filtro de mes específico.');
          toast.error('Tiempo de espera agotado', { id: 'export-excel' });
          return;
        }
        
        if (attempt < maxRetries) {
          const delay = baseDelay * Math.pow(2, attempt) + Math.random() * 1000;
          console.warn(`[Export] Network error, retry ${attempt + 1}/${maxRetries}:`, err.message);
          toast.loading(`Error de red, reintentando...`, { id: 'export-excel' });
          await new Promise(r => setTimeout(r, delay));
          continue;
        }
      }
    }
    
    console.error('[Export] All retries exhausted:', lastError);
    setMessage(`❌ Error de conexión después de ${maxRetries + 1} intentos. Verifique su conexión.`);
    toast.error('Error de conexión persistente', { id: 'export-excel' });
    setExportando(false);
  };

  // Asegurar que exportando se resetea si la función termina por cualquier motivo
  const handleExportExcel = async () => {
    try {
      await exportExcel();
    } finally {
      setExportando(false);
    }
  };

  const productosConPrecio = Object.entries(captureData.productos_precios)
    .filter(([_, data]) => data.precio_competencia && parseFloat(data.precio_competencia) > 0).length;

  return (
    <div className="space-y-6">
      {/* Sub-tabs */}
      <div className="flex gap-2 border-b pb-2">
        <button
          onClick={() => setActiveSubTab('captura')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeSubTab === 'captura' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          📝 Nueva Captura
        </button>
        {/* Gestión de Capturas - Solo visible para administradores */}
        {userRole === 'admin' && (
          <button
            onClick={() => setActiveSubTab('gestion')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeSubTab === 'gestion' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            📋 Gestión de Capturas
          </button>
        )}
      </div>
      
      {/* Tab: Gestión de Capturas - Solo para admin */}
      {activeSubTab === 'gestion' && userRole === 'admin' && (
        <Suspense fallback={<ModuloCargando />}>
          <InformeInvestigacionManager />
        </Suspense>
      )}
      
      {/* Tab: Nueva Captura */}
      {activeSubTab === 'captura' && (
        <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6">
        <h2 className="text-2xl font-bold text-green-800 mb-2">📦 Captura Masiva de Productos</h2>
        <p className="text-green-600">Capture precios de todos los productos de un POS en una sola operación</p>
      </div>
      
      {/* Mensaje */}
      {message && (
        <div className={`p-4 rounded-lg ${
          message.includes('✅') ? 'bg-green-50 text-green-700 border border-green-200' : 
          message.includes('⚠️') ? 'bg-yellow-50 text-yellow-700 border border-yellow-200' :
          'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {message}
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Selección de POS y DÍA */}
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Selector de POS */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">📍 POS (Punto de Venta) *</label>
              <select
                value={selectedPos}
                onChange={(e) => handlePosChange(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm"
                required
                data-testid="select-pos"
              >
                <option value="">Seleccionar POS...</option>
                {posOptions.map((pos, idx) => (
                  <option key={idx} value={pos}>{pos}</option>
                ))}
              </select>
              
              {posOptions.length === 0 && (
                <p className="text-sm text-yellow-600 mt-2">⚠️ No hay tiendas disponibles. Cargue el Maestro de Negociación primero.</p>
              )}
            </div>
            
            {/* Selector de DÍA */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">📅 Día *</label>
              <select
                value={selectedDia}
                onChange={(e) => handleDiaChange(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                required
                disabled={!selectedPos || diasOptions.length === 0}
                data-testid="select-dia"
              >
                <option value="">
                  {!selectedPos ? 'Primero seleccione un POS' : 
                   loadingDias ? 'Cargando días...' : 
                   diasOptions.length === 0 ? 'No hay días disponibles' : 
                   'Seleccionar día...'}
                </option>
                {diasOptions.map((dia, idx) => (
                  <option key={idx} value={dia}>{dia}</option>
                ))}
              </select>
              
              {loadingDias && (
                <div className="mt-2 flex items-center text-green-600 text-sm">
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-green-600 mr-2"></div>
                  Cargando días...
                </div>
              )}
            </div>
          </div>
          
          {loadingPos && (
            <div className="mt-3 flex items-center text-green-600">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 mr-2"></div>
              Cargando productos...
            </div>
          )}
        </div>
        
        {/* Datos comunes - Mostrar cuando hay POS seleccionado */}
        {selectedPos && productos.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  🎯 Estrategia * {captureData.estrategia && <span className="text-green-600 text-xs">(Auto-seleccionada)</span>}
                </label>
                <select
                  value={captureData.estrategia}
                  onChange={(e) => setCaptureData({...captureData, estrategia: e.target.value})}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 text-sm ${
                    captureData.estrategia ? 'border-green-400 bg-green-50' : 'border-gray-300'
                  }`}
                  required
                  data-testid="select-estrategia"
                >
                  <option value="">Seleccionar estrategia...</option>
                  {estrategias.map((est, idx) => (
                    <option key={idx} value={est}>{est}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">🏪 Competidor *</label>
                <select
                  value={captureData.competidor}
                  onChange={(e) => setCaptureData({...captureData, competidor: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm"
                  required
                  data-testid="select-competidor"
                >
                  <option value="">Seleccionar competidor...</option>
                  {competidores.map((comp, idx) => (
                    <option key={idx} value={comp}>{comp}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}
        
        {/* Lista de Productos */}
        {selectedPos && productos.length > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-6">
            <h3 className="text-lg font-bold text-green-800 mb-4">
              📋 Productos para {selectedPos} ({productos.length} productos)
            </h3>
            
            <div className="space-y-4 max-h-[500px] overflow-y-auto">
              {productos.map((producto, idx) => (
                <div key={idx} className="bg-white p-4 rounded-lg border border-green-200">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
                    {/* Info del producto */}
                    <div>
                      <div className="font-bold text-gray-800">{producto.IDPRODUCTO}</div>
                      <div className="text-sm text-gray-600 truncate">{producto.ITEMS}</div>
                      <div className="text-xs text-blue-600">Lab: {producto.LABORATORIO}</div>
                      {producto.ESTRATEGIA && (
                        <div className="text-xs text-purple-600">Est: {producto.ESTRATEGIA}</div>
                      )}
                    </div>
                    
                    {/* PVP */}
                    <div className="text-center">
                      <div className="text-sm font-semibold text-green-700">PVP</div>
                      <div className="text-xl font-bold text-green-600">${producto.PVP?.toFixed(2)}</div>
                    </div>
                    
                    {/* Precio Competencia */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Precio Competencia *</label>
                      {(() => {
                        const precio = parseFloat(captureData.productos_precios[producto.IDPRODUCTO]?.precio_competencia || 0);
                        const pvp = producto.PVP || 0;
                        const descuento = pvp > 0 && precio > 0 ? ((pvp - precio) / pvp) * 100 : 0;
                        const isNegative = precio < 0;
                        const isDescuentoNegativo = precio > pvp && pvp > 0; // Descuento negativo = precio > PVP
                        const isExcessive = descuento > 70;
                        const hasError = isNegative || isDescuentoNegativo || isExcessive;
                        
                        return (
                          <>
                            <input
                              type="number"
                              step="0.01"
                              value={captureData.productos_precios[producto.IDPRODUCTO]?.precio_competencia || ''}
                              onChange={(e) => updateProductPrice(producto.IDPRODUCTO, 'precio_competencia', e.target.value)}
                              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 text-sm ${
                                hasError 
                                  ? 'border-red-500 bg-red-50 focus:ring-red-500 text-red-800' 
                                  : 'border-gray-300 focus:ring-green-500'
                              }`}
                              placeholder="0.00"
                              data-testid={`input-precio-${producto.IDPRODUCTO}`}
                            />
                            {/* Mensajes de error y descuento */}
                            {captureData.productos_precios[producto.IDPRODUCTO]?.precio_competencia && (
                              <div className="mt-1 space-y-1">
                                {isNegative && (
                                  <p className="text-xs text-red-600 font-bold bg-red-100 px-2 py-1 rounded">
                                    🚫 PRECIO NEGATIVO - No permitido
                                  </p>
                                )}
                                {isDescuentoNegativo && (
                                  <p className="text-xs text-red-600 font-bold bg-red-100 px-2 py-1 rounded">
                                    🚫 DESCUENTO NEGATIVO ({descuento.toFixed(1)}%) - Precio mayor que PVP
                                  </p>
                                )}
                                {isExcessive && !isNegative && !isDescuentoNegativo && (
                                  <p className="text-xs text-red-600 font-bold bg-red-100 px-2 py-1 rounded">
                                    🚫 DESCUENTO {descuento.toFixed(1)}% - Excede límite de 70%
                                  </p>
                                )}
                                {!hasError && (
                                  <p className="text-xs text-green-600">
                                    ✅ Descuento: {descuento.toFixed(1)}%
                                  </p>
                                )}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                    
                    {/* Observaciones */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Observaciones</label>
                      <input
                        type="text"
                        value={captureData.productos_precios[producto.IDPRODUCTO]?.observaciones_especificas || ''}
                        onChange={(e) => updateProductPrice(producto.IDPRODUCTO, 'observaciones_especificas', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm"
                        placeholder="Opcional..."
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Observaciones Generales */}
        {selectedPos && productos.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <label className="block text-sm font-bold text-gray-700 mb-2">📝 Observaciones Generales</label>
            <textarea
              value={captureData.observaciones}
              onChange={(e) => setCaptureData({...captureData, observaciones: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm"
              rows={3}
              placeholder="Observaciones que aplicarán a todos los productos..."
            />
          </div>
        )}
        
        {/* Sección de Archivos Adjuntos */}
        {selectedPos && productos.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <label className="block text-sm font-bold text-gray-700">📎 Archivos Adjuntos (Imágenes / PDF)</label>
              <span className="text-xs text-gray-500">Máx. 10MB por archivo</span>
            </div>
            
            {/* Input de archivo */}
            <div className="flex items-center gap-4 mb-4">
              <label className="flex-1 cursor-pointer">
                <div className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                  uploadingFile ? 'border-gray-300 bg-gray-50' : 'border-blue-300 hover:border-blue-500 hover:bg-blue-50'
                }`}>
                  {uploadingFile ? (
                    <div className="flex items-center justify-center gap-2 text-gray-500">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                      <span>Subiendo archivo...</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2 text-gray-600">
                      <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <span className="text-sm font-medium">Clic para subir archivo</span>
                      <span className="text-xs text-gray-400">JPG, PNG, GIF, WEBP o PDF</span>
                    </div>
                  )}
                </div>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                  onChange={handleFileUpload}
                  disabled={uploadingFile}
                  className="hidden"
                  data-testid="input-file-upload"
                />
              </label>
            </div>
            
            {/* Lista de archivos adjuntos */}
            {archivosAdjuntos.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700 mb-2">Archivos subidos ({archivosAdjuntos.length}):</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {archivosAdjuntos.map((archivo, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                      {archivo.is_image ? (
                        <img 
                          src={`${API}${archivo.url}`} 
                          alt={archivo.original_name}
                          className="w-12 h-12 object-cover rounded"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-red-100 rounded flex items-center justify-center">
                          <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{archivo.original_name}</p>
                        <a 
                          href={`${API}${archivo.url}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:underline"
                        >
                          Ver archivo
                        </a>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(archivo.filename)}
                        className="p-1 text-red-500 hover:bg-red-100 rounded transition-colors"
                        title="Eliminar"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Botones de acción */}
        {productos.length > 0 && (
          <div className="flex gap-4">
            <button
              type="submit"
              disabled={submitting || productosConPrecio === 0}
              className="flex-1 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-bold py-4 px-6 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="btn-guardar"
            >
              {submitting ? '⏳ Guardando...' : `💾 Guardar Investigación (${productosConPrecio} Productos)`}
            </button>
          </div>
        )}
      </form>
      
      {/* Sección de Exportación con Filtro de Mes - Solo para roles con permiso */}
      {canExport && (
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">📥 Exportar Investigaciones</h3>
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-bold text-gray-700 mb-2">📅 Filtrar por Mes</label>
              <select
                value={mesExport}
                onChange={(e) => setMesExport(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                data-testid="select-mes-export"
                disabled={exportando}
              >
                <option value="">Todos los meses</option>
                {mesesDisponibles.map((mes, idx) => (
                  <option key={idx} value={mes}>{mes}</option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={handleExportExcel}
              disabled={exportando}
              className={`px-6 py-2 font-bold rounded-lg transition-all flex items-center gap-2 ${
                exportando 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
              data-testid="btn-export"
            >
              {exportando ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Descargando...
                </>
              ) : (
                <>📥 Exportar Excel</>
              )}
            </button>
          </div>
          {mesesDisponibles.length === 0 && (
            <p className="text-sm text-gray-500 mt-2">No hay datos de investigación guardados aún.</p>
          )}
          {exportando && (
            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-700 flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Generando archivo Excel... Por favor espere, esto puede tardar varios minutos.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Modal de Verificación de Captura Exitosa */}
      {showSuccessModal && captureResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
            {/* Header con gradiente verde */}
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-6 text-center">
              <div className="w-20 h-20 bg-white rounded-full mx-auto flex items-center justify-center mb-4 shadow-lg">
                <svg className="w-12 h-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-white mb-1">¡Investigación Guardada Exitosamente!</h2>
              <p className="text-green-100">Captura masiva completada correctamente</p>
            </div>
            
            {/* Estadísticas */}
            <div className="p-6">
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-green-50 rounded-xl p-4 text-center border border-green-200">
                  <p className="text-3xl font-bold text-green-600">{captureResult.productosGuardados}</p>
                  <p className="text-sm text-green-700">Productos Guardados</p>
                </div>
                <div className="bg-blue-50 rounded-xl p-4 text-center border border-blue-200">
                  <p className="text-3xl font-bold text-blue-600">{captureResult.totalProcesados}</p>
                  <p className="text-sm text-blue-700">Total Procesados</p>
                </div>
              </div>
              
              {/* Detalles de la captura */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                <h3 className="font-bold text-gray-800 border-b pb-2">📋 Detalles de la Captura</h3>
                
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-500">POS:</span>
                    <span className="font-semibold text-gray-800 ml-2">{captureResult.pos}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Estrategia:</span>
                    <span className="font-semibold text-gray-800 ml-2">{captureResult.estrategia}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Competidor:</span>
                    <span className="font-semibold text-gray-800 ml-2">{captureResult.competidor}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Fecha:</span>
                    <span className="font-semibold text-gray-800 ml-2">{captureResult.fecha}</span>
                  </div>
                  {captureResult.archivosAdjuntos > 0 && (
                    <div className="col-span-2">
                      <span className="text-gray-500">📎 Archivos adjuntos:</span>
                      <span className="font-semibold text-blue-600 ml-2">{captureResult.archivosAdjuntos} archivo(s)</span>
                    </div>
                  )}
                </div>
                
                <div className="pt-2 border-t">
                  <span className="text-gray-500 text-sm">Hora de procesamiento:</span>
                  <span className="font-mono text-gray-800 ml-2">{captureResult.hora}</span>
                </div>
              </div>
              
              {/* Mensaje de confirmación */}
              <div className="mt-4 p-3 bg-green-100 rounded-lg border border-green-200">
                <p className="text-green-800 text-sm flex items-center">
                  <span className="mr-2">✅</span>
                  <strong>Captura completada exitosamente</strong> - Los datos han sido guardados correctamente en la base de datos
                </p>
              </div>
              
              {/* Botón de cerrar */}
              <button
                onClick={() => setShowSuccessModal(false)}
                className="w-full mt-6 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-bold py-3 px-6 rounded-xl transition-all shadow-lg"
                data-testid="btn-cerrar-modal"
              >
                Continuar Capturando
              </button>
            </div>
          </div>
        </div>
      )}
        </div>
      )}
    </div>
  );
}


// ============================================
// LABORATORIO TAB
// ============================================
function LaboratorioTab() {
  const [status, setStatus] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [results, setResults] = useState(null);
  const [message, setMessage] = useState('');
  const [activeResultTab, setActiveResultTab] = useState('resumen');
  // Sistema de espacios múltiples
  const [espacios, setEspacios] = useState([]);
  const [miEspacio, setMiEspacio] = useState(null);
  const [espaciosDisponibles, setEspaciosDisponibles] = useState(3);
  const [loadingEspacios, setLoadingEspacios] = useState(true);
  const [liberandoEspacio, setLiberandoEspacio] = useState(false);
  
  // Estados para captura de resultados
  const [capturandoResultados, setCapturandoResultados] = useState(false);
  
  // Estados para data detallada
  const [dataDetallada, setDataDetallada] = useState(null);
  const [tipoDataDetallada, setTipoDataDetallada] = useState('facturas');
  const [paginaDetallada, setPaginaDetallada] = useState(1);
  const [loadingData, setLoadingData] = useState(false);
  const [soloConPerdida, setSoloConPerdida] = useState(true);
  
  const [params, setParams] = useState({
    periodo_inicio: '',
    periodo_fin: '',
    margen_minimo: 0.10,
    margen_objetivo: 0.25,
    umbral_descuento: 0.40,
    umbral_anomalia: 2.5,
    pareto_pct: 80,
    top_n_facturas: 20,
    top_n_productos: 20,
    top_n_promociones: 20,
    activar_recupero: false,
    activar_anomalias: false
  });

  useEffect(() => {
    fetchEspacios();
  }, []);
  
  // Cargar data detallada cuando cambia el tab o tipo
  useEffect(() => {
    if (activeResultTab === 'data-detallada' && miEspacio) {
      fetchDataDetallada();
    }
    if (activeResultTab === 'historial') {
      fetchHistorialCapturas();
    }
  }, [activeResultTab, tipoDataDetallada, paginaDetallada, soloConPerdida, miEspacio]);
  
  // Función para capturar resultados
  const capturarResultados = async () => {
    if (!results) return;
    
    setCapturandoResultados(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/laboratorio-utilidad/capturar-resultados?espacio_num=${miEspacio}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      
      if (data.success) {
        toast.success(`✅ Resultados capturados: ${data.resumen?.facturas_analizadas} facturas, $${data.resumen?.perdida_total?.toLocaleString()} pérdida`);
        setMessage(`✅ Captura guardada - ID: ${data.captura_id?.slice(-8)}`);
        // Refrescar historial
        fetchHistorialCapturas();
      } else {
        toast.error(`❌ ${data.detail || 'Error al capturar'}`);
      }
    } catch (err) {
      toast.error(`❌ Error: ${err.message}`);
    } finally {
      setCapturandoResultados(false);
    }
  };
  
  // Estado para búsqueda y historial
  const [busquedaData, setBusquedaData] = useState('');
  const [historialCapturas, setHistorialCapturas] = useState([]);
  const [loadingHistorial, setLoadingHistorial] = useState(false);
  
  // Función para obtener data detallada
  const fetchDataDetallada = async (searchOverride = null) => {
    if (!miEspacio) return;
    
    setLoadingData(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        tipo: tipoDataDetallada,
        page: paginaDetallada,
        page_size: 50,
        solo_con_perdida: soloConPerdida,
        busqueda: searchOverride !== null ? searchOverride : busquedaData,
        espacio_num: miEspacio
      });
      
      const res = await fetch(`${API}/api/laboratorio-utilidad/data-detallada?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      
      if (data.success) {
        setDataDetallada(data);
      } else {
        console.error('Error fetching data:', data);
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoadingData(false);
    }
  };
  
  // Función para buscar con debounce
  const handleBusquedaChange = (value) => {
    setBusquedaData(value);
    setPaginaDetallada(1);
  };
  
  // Buscar cuando se presiona Enter o botón
  const ejecutarBusqueda = () => {
    setPaginaDetallada(1);
    fetchDataDetallada(busquedaData);
  };
  
  // Función para obtener historial de capturas
  const fetchHistorialCapturas = async () => {
    setLoadingHistorial(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/laboratorio-utilidad/historial-capturas?limit=20`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      
      if (data.success) {
        setHistorialCapturas(data.capturas || []);
      }
    } catch (err) {
      console.error('Error fetching historial:', err);
    } finally {
      setLoadingHistorial(false);
    }
  };
  
  // Cargar estado de espacios
  const fetchEspacios = async () => {
    setLoadingEspacios(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/laboratorio-utilidad/espacios`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setEspacios(data.espacios || []);
      setMiEspacio(data.mi_espacio);
      setEspaciosDisponibles(data.disponibles || 0);
      
      // Si el usuario tiene un espacio asignado, cargar su status
      if (data.mi_espacio) {
        fetchStatus(data.mi_espacio);
      } else {
        setStatus(null);
      }
    } catch (err) {
      console.error('Error cargando espacios:', err);
    } finally {
      setLoadingEspacios(false);
    }
  };
  
  // Liberar espacio actual
  const liberarEspacio = async () => {
    if (!miEspacio) return;
    
    setLiberandoEspacio(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/laboratorio-utilidad/liberar-espacio?espacio_num=${miEspacio}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      
      if (data.success) {
        setMessage('✅ Espacio liberado correctamente');
        setStatus(null);
        setResults(null);
        setMiEspacio(null);
        fetchEspacios();
      } else {
        setMessage(`❌ ${data.message || 'Error al liberar espacio'}`);
      }
    } catch (err) {
      setMessage(`❌ Error: ${err.message}`);
    } finally {
      setLiberandoEspacio(false);
    }
  };

  const fetchStatus = async (espacioNum = null) => {
    try {
      const token = localStorage.getItem('token');
      const espacio = espacioNum || miEspacio;
      
      // Timeout de 2 minutos para obtener status
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000);
      
      const url = espacio 
        ? `${API}/api/laboratorio-utilidad/status?espacio_num=${espacio}`
        : `${API}/api/laboratorio-utilidad/status`;
      
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      const data = await res.json();
      
      // Manejar errores de timeout del backend
      if (data.error_type === 'timeout') {
        setMessage('⚠️ ' + data.message);
      } else if (data.error_type === 'connection') {
        setMessage('⚠️ ' + data.message);
      }
      
      setStatus(data);
    } catch (err) {
      if (err.name === 'AbortError') {
        setMessage('⚠️ La conexión tardó demasiado. Por favor, recargue la página.');
      } else {
        console.error(err);
        setMessage('⚠️ Error de conexión al servidor.');
      }
    }
  };

  const handleUpload = async (file) => {
    // Validar tamaño máximo (600MB)
    const maxSize = 600 * 1024 * 1024;
    if (file.size > maxSize) {
      setMessage(`❌ Archivo muy grande (${(file.size / 1024 / 1024).toFixed(1)}MB). Máximo permitido: 600MB`);
      return;
    }
    
    // Verificar si hay espacios disponibles (solo si el usuario no tiene uno asignado)
    if (!miEspacio && espaciosDisponibles === 0) {
      setMessage('❌ Todos los espacios de análisis están ocupados. Por favor, espere a que otro usuario libere su espacio o intente más tarde.');
      toast.error('No hay espacios disponibles', {
        description: 'Todos los 3 espacios de análisis están ocupados. Intente más tarde.',
        duration: 5000
      });
      return;
    }
    
    setUploading(true);
    setMessage(`⏳ Subiendo archivo (${(file.size / 1024 / 1024).toFixed(1)}MB)... Por favor espere, esto puede tardar varios minutos.`);
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 300000); // 5 minutos timeout para archivos grandes
      
      // Incluir espacio_num si el usuario ya tiene uno asignado
      const uploadUrl = miEspacio 
        ? `${API}/api/laboratorio-utilidad/upload?espacio_num=${miEspacio}`
        : `${API}/api/laboratorio-utilidad/upload`;
      
      const res = await fetch(uploadUrl, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await res.text();
        if (text.includes('503') || text.includes('Service Unavailable')) {
          setMessage('❌ El servidor está ocupado procesando el archivo. Espere 30 segundos e intente de nuevo.');
        } else {
          setMessage('❌ Error en la respuesta del servidor. Intente de nuevo.');
        }
        return;
      }
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        const espacioAsignado = data.espacio_num;
        setMiEspacio(espacioAsignado);
        setMessage(`✅ ${data.message} - ${data.records?.toLocaleString()} registros cargados en Espacio ${espacioAsignado}`);
        toast.success(`Archivo cargado en Espacio ${espacioAsignado}`, {
          description: `${data.records?.toLocaleString()} registros listos para analizar`
        });
        fetchEspacios();
        fetchStatus(espacioAsignado);
        setResults(null);
      } else {
        setMessage(`❌ ${data.detail || data.message || 'Error al cargar archivo'}`);
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        setMessage('❌ Tiempo de espera agotado. El archivo es muy grande, intente con un archivo más pequeño.');
      } else if (err.message.includes('Failed to fetch')) {
        setMessage('❌ Error de conexión. El servidor puede estar reiniciando. Espere 15 segundos e intente de nuevo.');
      } else {
        setMessage(`❌ Error: ${err.message}`);
      }
    } finally {
      setUploading(false);
    }
  };

  const ejecutarAnalisis = async () => {
    if (!miEspacio) {
      setMessage('❌ No tienes ningún archivo cargado. Sube un archivo primero.');
      return;
    }
    
    setAnalyzing(true);
    setMessage('⏳ Analizando datos... Esto puede tardar hasta 15 minutos para archivos muy grandes (2M+ registros).');
    try {
      const token = localStorage.getItem('token');
      
      // Timeout de 15 minutos para el análisis de archivos grandes
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 900000);
      
      // Incluir espacio_num en la URL
      const res = await fetch(`${API}/api/laboratorio-utilidad/analizar?espacio_num=${miEspacio}`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(params),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      // Verificar si la respuesta es JSON válido
      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await res.text();
        if (text.includes('timed out') || text.includes('timeout')) {
          setMessage('❌ La base de datos está tardando en responder. Por favor, espere 30 segundos e intente de nuevo.');
        } else if (text.includes('503') || text.includes('Service Unavailable')) {
          setMessage('❌ El servidor está ocupado. Espere 30 segundos e intente de nuevo.');
        } else {
          setMessage('❌ Error en la respuesta del servidor. Intente de nuevo.');
        }
        return;
      }
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        setResults(data);
        setMessage('✅ Análisis completado exitosamente');
        setActiveResultTab('resumen');
      } else {
        // Mensaje más amigable para errores de timeout
        const errorMsg = data.detail || 'Error en el análisis';
        if (errorMsg.toLowerCase().includes('timed out') || errorMsg.toLowerCase().includes('timeout')) {
          setMessage('❌ La base de datos está tardando en responder. Por favor, espere 30 segundos e intente de nuevo.');
        } else {
          setMessage(`❌ ${errorMsg}`);
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        setMessage('❌ El análisis tardó demasiado. Intente con un archivo más pequeño o espere unos minutos.');
      } else if (err.message.includes('Failed to fetch')) {
        setMessage('❌ Error de conexión. El servidor puede estar ocupado. Espere 30 segundos e intente de nuevo.');
      } else {
        setMessage(`❌ Error: ${err.message}`);
      }
    } finally {
      setAnalyzing(false);
    }
  };

  const exportExcel = async () => {
    if (!miEspacio) {
      setMessage('❌ No tienes ningún archivo cargado para exportar.');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      // Incluir pareto_pct y espacio_num en la URL
      const paretoPct = params.pareto_pct || 80;
      const res = await fetch(`${API}/api/laboratorio-utilidad/export-excel?pareto_pct=${paretoPct}&espacio_num=${miEspacio}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Laboratorio_Pareto${paretoPct}_Espacio${miEspacio}_${new Date().toISOString().slice(0,10)}.xlsx`;
        a.click();
        setMessage(`✅ Excel Pareto ${paretoPct}% exportado correctamente`);
      } else {
        const errData = await res.json().catch(() => ({}));
        setMessage(`❌ Error al exportar: ${errData.detail || 'Error desconocido'}`);
      }
    } catch (err) {
      setMessage(`❌ Error: ${err.message}`);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-xl p-6">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="text-2xl font-bold text-red-800 mb-2">🔬 Laboratorio de Análisis de Pérdida de Utilidad</h3>
            <p className="text-red-600">Identifica facturas, productos y promociones que generan pérdida de utilidad</p>
          </div>
          {/* Indicador de espacio asignado */}
          {miEspacio && (
            <div className="bg-green-100 border border-green-300 rounded-lg px-4 py-2 text-sm">
              <span className="text-green-800 font-semibold">📍 Tu Espacio: {miEspacio}</span>
            </div>
          )}
        </div>
      </div>
      
      {/* Panel de Espacios de Trabajo */}
      <div className="bg-white border border-gray-200 rounded-xl p-6" data-testid="panel-espacios">
        <div className="flex justify-between items-center mb-4">
          <h4 className="text-lg font-bold text-gray-800">🏢 Espacios de Trabajo Disponibles</h4>
          <button 
            onClick={fetchEspacios}
            className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
            data-testid="btn-refresh-espacios"
          >
            🔄 Actualizar
          </button>
        </div>
        
        {loadingEspacios ? (
          <div className="text-center py-4 text-gray-500">⏳ Cargando espacios...</div>
        ) : (
          <>
            {/* Grid de espacios */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {espacios.map((espacio) => (
                <div 
                  key={espacio.numero}
                  data-testid={`espacio-${espacio.numero}`}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    espacio.es_mio 
                      ? 'border-green-500 bg-green-50 ring-2 ring-green-200' 
                      : espacio.ocupado 
                        ? 'border-red-300 bg-red-50' 
                        : 'border-gray-200 bg-gray-50 hover:border-blue-300'
                  }`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <span className="font-bold text-lg">
                      Espacio {espacio.numero}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                      espacio.es_mio 
                        ? 'bg-green-500 text-white' 
                        : espacio.ocupado 
                          ? 'bg-red-400 text-white' 
                          : 'bg-green-400 text-white'
                    }`}>
                      {espacio.es_mio ? '✓ Tu espacio' : espacio.ocupado ? 'Ocupado' : 'Disponible'}
                    </span>
                  </div>
                  
                  {espacio.ocupado ? (
                    <div className="text-sm space-y-1">
                      <p className="text-gray-600">
                        <span className="font-medium">Usuario:</span> {espacio.usuario}
                      </p>
                      {espacio.filename && (
                        <p className="text-gray-600 truncate" title={espacio.filename}>
                          <span className="font-medium">Archivo:</span> {espacio.filename}
                        </p>
                      )}
                      {espacio.records > 0 && (
                        <p className="text-gray-600">
                          <span className="font-medium">Registros:</span> {espacio.records.toLocaleString()}
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">Listo para usar</p>
                  )}
                  
                  {/* Botón para liberar espacio propio */}
                  {espacio.es_mio && (
                    <button
                      onClick={liberarEspacio}
                      disabled={liberandoEspacio}
                      className="mt-3 w-full px-3 py-2 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 transition-colors disabled:bg-gray-400"
                      data-testid={`btn-liberar-espacio-${espacio.numero}`}
                    >
                      {liberandoEspacio ? '⏳ Liberando...' : '🗑️ Liberar Espacio'}
                    </button>
                  )}
                </div>
              ))}
            </div>
            
            {/* Mensaje de estado */}
            <div className={`text-center p-3 rounded-lg ${
              espaciosDisponibles === 0 
                ? 'bg-red-100 text-red-700 border border-red-200' 
                : espaciosDisponibles === 3 
                  ? 'bg-green-100 text-green-700 border border-green-200'
                  : 'bg-yellow-100 text-yellow-700 border border-yellow-200'
            }`}>
              {espaciosDisponibles === 0 ? (
                <span className="font-semibold">⚠️ Todos los espacios están ocupados. Espere a que otro usuario libere su espacio.</span>
              ) : (
                <span>{espaciosDisponibles} de 3 espacios disponibles</span>
              )}
            </div>
          </>
        )}
      </div>
      
      {/* Mensaje */}
      {message && (
        <div className={`p-4 rounded-lg ${message.includes('✅') ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          {message}
        </div>
      )}
      
      {/* Paso 1: Cargar Archivo */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h4 className="text-lg font-bold text-gray-800 mb-4">📤 Paso 1: Cargar Archivo de Facturas</h4>
        
        <div className="flex items-center gap-4">
          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={(e) => e.target.files[0] && handleUpload(e.target.files[0])}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-red-50 file:text-red-700 hover:file:bg-red-100"
            disabled={uploading || (espaciosDisponibles === 0 && !miEspacio)}
            data-testid="input-file-laboratorio"
          />
          {uploading && <span className="text-gray-500">⏳ Cargando...</span>}
          {espaciosDisponibles === 0 && !miEspacio && (
            <span className="text-red-500 text-sm ml-2">⚠️ Todos los espacios ocupados</span>
          )}
        </div>
        
        {/* Mostrar datos solo si hay un espacio asignado y datos cargados */}
        {status?.loaded && miEspacio && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex justify-between items-start">
              <p className="font-semibold text-green-800">✅ Archivo cargado: {status.filename || 'facturas.csv'}</p>
              <span className="text-xs bg-green-600 text-white px-2 py-1 rounded">Espacio {miEspacio}</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-2 text-sm">
              <div>
                <span className="text-gray-600">Registros:</span>
                <span className="font-bold ml-2">{(status.estadisticas?.registros || status.estadisticas?.total_registros || 0).toLocaleString()}</span>
              </div>
              <div>
                <span className="text-gray-600">Facturas:</span>
                <span className="font-bold ml-2">{(status.estadisticas?.facturas || status.estadisticas?.total_facturas || 0).toLocaleString()}</span>
              </div>
              <div>
                <span className="text-gray-600">Productos:</span>
                <span className="font-bold ml-2">{(status.estadisticas?.productos || status.estadisticas?.total_productos || 0).toLocaleString()}</span>
              </div>
              <div>
                <span className="text-gray-600 text-red-600">Con pérdida:</span>
                <span className="font-bold ml-2 text-red-600">{(status.estadisticas?.con_perdida || status.estadisticas?.registros_utilidad_negativa || 0).toLocaleString()}</span>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Paso 2: Configurar Parámetros - Solo mostrar si hay archivo cargado EN un espacio */}
      {status?.loaded && miEspacio && (
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h4 className="text-lg font-bold text-gray-800 mb-4">⚙️ Paso 2: Configurar Parámetros del Análisis</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Margen Mínimo (%)</label>
              <input
                type="number"
                step="0.01"
                value={params.margen_minimo}
                onChange={(e) => setParams({...params, margen_minimo: parseFloat(e.target.value)})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                data-testid="input-margen-minimo"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Margen Objetivo (%)</label>
              <input
                type="number"
                step="0.01"
                value={params.margen_objetivo}
                onChange={(e) => setParams({...params, margen_objetivo: parseFloat(e.target.value)})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Umbral Descuento Excesivo (%)</label>
              <input
                type="number"
                step="0.01"
                value={params.umbral_descuento}
                onChange={(e) => setParams({...params, umbral_descuento: parseFloat(e.target.value)})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                data-testid="input-umbral-descuento"
              />
            </div>
          </div>
          
          {/* Campo Pareto destacado */}
          <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <label className="block text-sm font-bold text-yellow-800 mb-2">
              📊 Análisis Pareto (80/20) - % de Pérdida a Incluir
            </label>
            <div className="flex items-center gap-4">
              <input
                type="number"
                min="10"
                max="100"
                value={params.pareto_pct}
                onChange={(e) => setParams({...params, pareto_pct: parseInt(e.target.value) || 80})}
                className="w-32 px-3 py-2 border-2 border-yellow-400 rounded-lg focus:ring-2 focus:ring-yellow-500 text-lg font-bold text-center"
                data-testid="input-pareto-pct"
              />
              <span className="text-sm text-yellow-700">
                % (El Excel incluirá los registros que representan este % de la pérdida total)
              </span>
            </div>
            <p className="text-xs text-yellow-600 mt-2">
              Ejemplo: 80% = Incluye facturas/productos que generan el 80% de la pérdida (Principio de Pareto)
            </p>
          </div>
          
          <div className="flex items-center gap-6 mt-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={params.activar_recupero}
                onChange={(e) => setParams({...params, activar_recupero: e.target.checked})}
                className="rounded text-red-600 focus:ring-red-500"
              />
              <span className="text-sm text-gray-700">Analizar Recupero</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={params.activar_anomalias}
                onChange={(e) => setParams({...params, activar_anomalias: e.target.checked})}
                className="rounded text-red-600 focus:ring-red-500"
              />
              <span className="text-sm text-gray-700">Analizar Anomalías Históricas</span>
            </label>
          </div>
          
          <div className="flex gap-4 mt-6">
            <button
              onClick={ejecutarAnalisis}
              disabled={analyzing}
              className="px-6 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 disabled:bg-gray-400 transition-colors"
              data-testid="btn-ejecutar-analisis"
            >
              {analyzing ? '⏳ Analizando...' : '🔍 Ejecutar Análisis'}
            </button>
            {results && (
              <button
                onClick={exportExcel}
                className="px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors"
                data-testid="btn-export-laboratorio"
              >
                📥 Exportar Excel
              </button>
            )}
            
            {/* Botón para capturar resultados */}
            {results && (
              <button
                onClick={capturarResultados}
                disabled={capturandoResultados}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2"
                data-testid="btn-capturar-resultados"
              >
                {capturandoResultados ? '⏳ Guardando...' : '💾 Capturar Resultados'}
              </button>
            )}
          </div>
        </div>
      )}
      
      {/* Paso 3: Resultados */}
      {results && (
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h4 className="text-lg font-bold text-gray-800 mb-4">📊 Paso 3: Resultados del Análisis</h4>
          
          {/* Tabs de resultados - CON DATA DETALLADA E HISTORIAL */}
          <div className="flex flex-wrap gap-2 mb-4 border-b pb-2">
            {[
              { id: 'resumen', label: '📈 Resumen' },
              { id: 'recomendaciones', label: '💡 Recomendaciones' },
              { id: 'data-detallada', label: '📊 Data Detallada' },
              { id: 'facturas', label: '🧾 Top Facturas' },
              { id: 'productos', label: '📦 Top Productos' },
              { id: 'promociones', label: '🏷️ Top Promociones' },
              { id: 'historial', label: '📜 Historial Capturas' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveResultTab(tab.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeResultTab === tab.id
                    ? 'bg-red-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          
          {/* Tab: Resumen Ejecutivo */}
          {activeResultTab === 'resumen' && (
            <div className="space-y-4">
              {/* KPIs principales */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                  <p className="text-sm text-red-600">Pérdida Total</p>
                  <p className="text-2xl font-bold text-red-800">${results.resumen?.perdida_total?.toLocaleString()}</p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                  <p className="text-sm text-blue-600">Venta Total</p>
                  <p className="text-2xl font-bold text-blue-800">${results.resumen?.venta_total?.toLocaleString()}</p>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <p className="text-sm text-green-600">Utilidad Total</p>
                  <p className="text-2xl font-bold text-green-800">${results.resumen?.utilidad_total?.toLocaleString()}</p>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
                  <p className="text-sm text-purple-600">Margen General</p>
                  <p className="text-2xl font-bold text-purple-800">{results.resumen?.margen_general?.toFixed(2)}%</p>
                </div>
              </div>
              
              {/* Métricas secundarias */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-600">Facturas con Pérdida</p>
                  <p className="text-lg font-bold text-gray-800">{results.resumen?.facturas_con_perdida?.toLocaleString()}</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-600">Productos con Pérdida</p>
                  <p className="text-lg font-bold text-gray-800">{results.resumen?.productos_con_perdida?.toLocaleString()}</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-600">Total Promociones</p>
                  <p className="text-lg font-bold text-gray-800">{results.resumen?.total_promociones?.toLocaleString()}</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-600">Registros Analizados</p>
                  <p className="text-lg font-bold text-gray-800">{results.resumen?.total_registros?.toLocaleString()}</p>
                </div>
              </div>
              
              {/* Top Causas de Pérdida */}
              {results.top_causas && results.top_causas.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h5 className="font-bold text-yellow-800 mb-2">⚠️ Principales Causas de Pérdida (Drivers)</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {results.top_causas.map((causa, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-white px-3 py-2 rounded border border-yellow-200">
                        <span className="font-medium text-yellow-800">{idx + 1}. {causa.causa}</span>
                        <span className="text-sm bg-yellow-200 px-2 py-1 rounded">{causa.ocurrencias?.toLocaleString()} ocurrencias</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Resumen de Top 3 */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {results.top_productos && results.top_productos[0] && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <h6 className="font-bold text-orange-800 text-sm mb-2">🏆 Producto más crítico</h6>
                    <p className="text-orange-700 font-mono text-sm">{results.top_productos[0].IdProducto}</p>
                    <p className="text-orange-600 text-xs truncate">{results.top_productos[0].Producto}</p>
                    <p className="text-red-600 font-bold mt-1">Pérdida: ${parseFloat(results.top_productos[0].Perdida || 0).toLocaleString()}</p>
                  </div>
                )}
                {results.top_promociones && results.top_promociones[0] && (
                  <div className="bg-pink-50 border border-pink-200 rounded-lg p-4">
                    <h6 className="font-bold text-pink-800 text-sm mb-2">🏷️ Promoción más crítica</h6>
                    <p className="text-pink-700 font-mono text-sm">{results.top_promociones[0].IdPlan}</p>
                    <p className="text-pink-600 text-xs truncate">{results.top_promociones[0]['Descripcion Plan'] || results.top_promociones[0].DescripcionPlan}</p>
                    <p className="text-red-600 font-bold mt-1">Pérdida: ${parseFloat(results.top_promociones[0].Perdida || 0).toLocaleString()}</p>
                  </div>
                )}
                {results.top_zonas && results.top_zonas[0] && (
                  <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
                    <h6 className="font-bold text-cyan-800 text-sm mb-2">🗺️ Zona más crítica</h6>
                    <p className="text-cyan-700 font-medium">{results.top_zonas[0].Zona}</p>
                    <p className="text-cyan-600 text-xs">{results.top_zonas[0].Facturas} facturas afectadas</p>
                    <p className="text-red-600 font-bold mt-1">Pérdida: ${parseFloat(results.top_zonas[0].Perdida || 0).toLocaleString()}</p>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Tab: Recomendaciones - NUEVA */}
          {activeResultTab === 'recomendaciones' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 mb-4">Acciones recomendadas basadas en el análisis de pérdida de utilidad</p>
              
              {/* Recomendaciones basadas en causas */}
              {results.top_causas && results.top_causas.length > 0 && (
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-5">
                  <h5 className="font-bold text-amber-800 mb-4 flex items-center gap-2">
                    <span className="text-2xl">⚠️</span> Acciones Prioritarias por Causa de Pérdida
                  </h5>
                  <div className="space-y-3">
                    {results.top_causas.slice(0, 5).map((causa, idx) => (
                      <div key={idx} className="bg-white rounded-lg p-4 border border-amber-100 shadow-sm">
                        <div className="flex justify-between items-start mb-2">
                          <span className="font-bold text-amber-900">#{idx + 1} {causa.causa}</span>
                          <span className="text-xs bg-amber-200 text-amber-800 px-2 py-1 rounded-full">
                            {causa.ocurrencias?.toLocaleString()} casos
                          </span>
                        </div>
                        <p className="text-sm text-gray-700">
                          {causa.causa === 'MARGEN_BAJO' && '💡 Revisar estructura de precios y costos. Considerar ajuste de PVP o renegociación con proveedores.'}
                          {causa.causa === 'DESCUENTO_EXCESIVO' && '💡 Evaluar políticas de descuento. Limitar descuentos máximos o requerir aprobación especial.'}
                          {causa.causa === 'UTILIDAD_NEGATIVA' && '💡 Identificar productos vendidos bajo costo. Revisar precios de compra y venta inmediatamente.'}
                          {causa.causa === 'PROMOCION_AGRESIVA' && '💡 Revisar ROI de promociones. Ajustar mecánicas promocionales para proteger margen mínimo.'}
                          {causa.causa === 'COSTO_ALTO' && '💡 Negociar mejores condiciones con proveedores o buscar alternativas de abastecimiento.'}
                          {!['MARGEN_BAJO', 'DESCUENTO_EXCESIVO', 'UTILIDAD_NEGATIVA', 'PROMOCION_AGRESIVA', 'COSTO_ALTO'].includes(causa.causa) && 
                            '💡 Analizar en detalle esta causa para implementar acciones correctivas específicas.'}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Recomendaciones por productos críticos */}
              {results.top_productos && results.top_productos.length > 0 && (
                <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-xl p-5">
                  <h5 className="font-bold text-red-800 mb-4 flex items-center gap-2">
                    <span className="text-2xl">📦</span> Productos que Requieren Atención Inmediata
                  </h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {results.top_productos.slice(0, 4).map((p, idx) => (
                      <div key={idx} className="bg-white rounded-lg p-4 border border-red-100 shadow-sm">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-mono text-xs text-gray-500">{p.IdProducto}</p>
                            <p className="font-medium text-gray-800 text-sm truncate max-w-[200px]">{p.Producto}</p>
                          </div>
                          <span className="text-red-600 font-bold">-${parseFloat(p.Perdida || 0).toLocaleString()}</span>
                        </div>
                        <div className="mt-2 text-xs text-gray-600">
                          <span className="bg-gray-100 px-2 py-1 rounded mr-2">Margen: {parseFloat(p['Margen_%'] || 0).toFixed(1)}%</span>
                          <span className="bg-gray-100 px-2 py-1 rounded">{p.Facturas} facturas</span>
                        </div>
                        <p className="mt-2 text-xs text-red-700 italic">
                          → {p.Driver === 'MARGEN_BAJO' ? 'Revisar precio de venta' : 
                             p.Driver === 'DESCUENTO_EXCESIVO' ? 'Limitar descuentos' :
                             p.Driver === 'UTILIDAD_NEGATIVA' ? 'Suspender venta o ajustar precio' : 'Analizar causa'}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Recomendaciones por promociones */}
              {results.top_promociones && results.top_promociones.length > 0 && (
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-5">
                  <h5 className="font-bold text-purple-800 mb-4 flex items-center gap-2">
                    <span className="text-2xl">🏷️</span> Promociones a Revisar
                  </h5>
                  <div className="space-y-3">
                    {results.top_promociones.slice(0, 3).map((promo, idx) => (
                      <div key={idx} className="bg-white rounded-lg p-4 border border-purple-100 shadow-sm">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-mono text-xs text-gray-500">{promo.IdPlan}</p>
                            <p className="font-medium text-gray-800">{promo['Descripcion Plan'] || promo.DescripcionPlan}</p>
                          </div>
                          <span className="text-red-600 font-bold">-${parseFloat(promo.Perdida || 0).toLocaleString()}</span>
                        </div>
                        <p className="mt-2 text-sm text-purple-700">
                          💡 Recomendación: Evaluar si el incremento en volumen compensa la pérdida de margen. 
                          Considerar ajustar mecánica o finalizar promoción.
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Resumen de acciones */}
              <div className="bg-gradient-to-r from-green-50 to-teal-50 border border-green-200 rounded-xl p-5">
                <h5 className="font-bold text-green-800 mb-3 flex items-center gap-2">
                  <span className="text-2xl">✅</span> Plan de Acción Sugerido
                </h5>
                <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
                  <li><strong>Corto plazo (1-7 días):</strong> Revisar los {results.resumen?.productos_con_perdida || 0} productos con pérdida y ajustar precios críticos.</li>
                  <li><strong>Mediano plazo (1-4 semanas):</strong> Renegociar condiciones con proveedores de productos de alto costo.</li>
                  <li><strong>Largo plazo (1-3 meses):</strong> Implementar políticas de descuento máximo y alertas de margen mínimo.</li>
                </ol>
                <div className="mt-4 p-3 bg-white rounded-lg border border-green-100">
                  <p className="text-sm text-green-800">
                    <strong>Impacto estimado:</strong> Corrigiendo el top 20% de productos críticos, 
                    se podría recuperar aproximadamente <span className="font-bold text-green-600">
                    ${((results.resumen?.perdida_total || 0) * 0.6).toLocaleString()}
                    </span> en utilidad.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Tab: Data Detallada - CON BÚSQUEDA */}
          {activeResultTab === 'data-detallada' && (
            <div className="space-y-4">
              {/* Controles de filtro y búsqueda */}
              <div className="flex flex-wrap items-center gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
                {/* Campo de búsqueda */}
                <div className="flex items-center gap-2 flex-1 min-w-[300px]">
                  <label className="text-sm font-medium text-gray-700 whitespace-nowrap">🔍 Buscar:</label>
                  <input
                    type="text"
                    value={busquedaData}
                    onChange={(e) => handleBusquedaChange(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && ejecutarBusqueda()}
                    placeholder={tipoDataDetallada === 'facturas' ? 'ID Factura, Producto...' : 
                               tipoDataDetallada === 'productos' ? 'ID Producto, Nombre...' : 
                               'ID Plan, Descripción...'}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm flex-1"
                    data-testid="input-busqueda-data"
                  />
                  <button
                    onClick={ejecutarBusqueda}
                    disabled={loadingData}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
                  >
                    Buscar
                  </button>
                  {busquedaData && (
                    <button
                      onClick={() => { setBusquedaData(''); fetchDataDetallada(''); }}
                      className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-300"
                    >
                      ✕ Limpiar
                    </button>
                  )}
                </div>
                
                {/* Selector de tipo de ordenamiento */}
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700">Ordenar por:</label>
                  <select
                    value={tipoDataDetallada}
                    onChange={(e) => { setTipoDataDetallada(e.target.value); setPaginaDetallada(1); setBusquedaData(''); }}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    data-testid="select-tipo-data"
                  >
                    <option value="facturas">🧾 Por Factura (mayor pérdida primero)</option>
                    <option value="productos">📦 Por Producto</option>
                    <option value="promociones">🏷️ Por Plan/Promoción</option>
                  </select>
                </div>
                
                {/* Filtro solo con pérdida */}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={soloConPerdida}
                    onChange={(e) => { setSoloConPerdida(e.target.checked); setPaginaDetallada(1); }}
                    className="w-4 h-4 text-red-600 rounded"
                  />
                  <span className="text-sm text-gray-700">Solo con pérdida</span>
                </label>
                
                {/* Indicador de carga */}
                {loadingData && (
                  <span className="text-sm text-blue-600 animate-pulse">⏳ Cargando...</span>
                )}
              </div>
              
              {/* Totales */}
              {dataDetallada?.totales && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                    <p className="text-xs text-blue-600">Venta (Página)</p>
                    <p className="text-lg font-bold text-blue-800">${dataDetallada.totales.venta_pagina?.toLocaleString(undefined, {maximumFractionDigits: 2})}</p>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                    <p className="text-xs text-red-600">Utilidad (Página)</p>
                    <p className="text-lg font-bold text-red-800">${dataDetallada.totales.utilidad_pagina?.toLocaleString(undefined, {maximumFractionDigits: 2})}</p>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
                    <p className="text-xs text-gray-600">Venta Total</p>
                    <p className="text-lg font-bold text-gray-800">${dataDetallada.totales.venta_total?.toLocaleString(undefined, {maximumFractionDigits: 2})}</p>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
                    <p className="text-xs text-gray-600">Utilidad Total</p>
                    <p className="text-lg font-bold text-gray-800">${dataDetallada.totales.utilidad_total?.toLocaleString(undefined, {maximumFractionDigits: 2})}</p>
                  </div>
                </div>
              )}
              
              {/* Info de columnas */}
              {dataDetallada?.columnas && (
                <div className="text-xs text-gray-500 mb-2">
                  📋 {dataDetallada.columnas.length} columnas disponibles | {dataDetallada.pagination?.total_records || 0} registros totales
                </div>
              )}
              
              {/* Tabla de datos - DINÁMICA con TODAS las columnas */}
              {dataDetallada?.data && dataDetallada.data.length > 0 ? (
                <div className="overflow-x-auto max-h-[600px] border rounded-lg">
                  <table className="w-full text-xs border-collapse" data-testid="tabla-data-detallada">
                    <thead className="bg-gray-100 sticky top-0 z-10">
                      <tr>
                        {dataDetallada.columnas.map((col, colIdx) => {
                          // Resaltar columnas importantes
                          const isUtilidad = col.toLowerCase().includes('utilidad') || col.toLowerCase().includes('perdida');
                          const isVenta = col.toLowerCase().includes('venta');
                          const isCosto = col.toLowerCase().includes('costo');
                          const isMargen = col.toLowerCase().includes('margen');
                          const isId = col.toLowerCase().includes('id') || col.toLowerCase().includes('codigo');
                          
                          return (
                            <th 
                              key={colIdx} 
                              className={`px-2 py-2 text-left border whitespace-nowrap ${
                                isUtilidad ? 'bg-red-100 text-red-700' :
                                isVenta ? 'bg-blue-100 text-blue-700' :
                                isCosto ? 'bg-orange-100 text-orange-700' :
                                isMargen ? 'bg-purple-100 text-purple-700' :
                                isId ? 'bg-gray-200' : ''
                              }`}
                            >
                              {col}
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {dataDetallada.data.map((row, rowIdx) => (
                        <tr key={rowIdx} className="border-b hover:bg-yellow-50">
                          {dataDetallada.columnas.map((col, colIdx) => {
                            const value = row[col];
                            const isUtilidad = col.toLowerCase().includes('utilidad') || col.toLowerCase().includes('perdida');
                            const isVenta = col.toLowerCase().includes('venta');
                            const isCosto = col.toLowerCase().includes('costo');
                            const isMargen = col.toLowerCase().includes('margen');
                            const isNumeric = typeof value === 'number' || (!isNaN(parseFloat(value)) && isFinite(value));
                            
                            // Formatear valor
                            let displayValue = value;
                            let cellClass = 'px-2 py-1 border';
                            
                            if (value === null || value === undefined || value === '') {
                              displayValue = '-';
                              cellClass += ' text-gray-400';
                            } else if (isNumeric) {
                              const numVal = parseFloat(value);
                              if (isUtilidad || isVenta || isCosto) {
                                displayValue = `$${numVal.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
                                if (isUtilidad && numVal < 0) {
                                  cellClass += ' text-red-600 font-bold bg-red-50';
                                } else if (isUtilidad && numVal >= 0) {
                                  cellClass += ' text-green-600';
                                }
                              } else if (isMargen) {
                                displayValue = `${numVal.toFixed(2)}%`;
                                if (numVal < 0) cellClass += ' text-red-600';
                              } else {
                                displayValue = numVal.toLocaleString(undefined, {maximumFractionDigits: 2});
                              }
                              cellClass += ' text-right';
                            } else {
                              // Es texto
                              displayValue = String(value);
                              if (displayValue.length > 30) {
                                displayValue = displayValue.substring(0, 30) + '...';
                              }
                            }
                            
                            return (
                              <td key={colIdx} className={cellClass} title={String(value)}>
                                {displayValue}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  {loadingData ? '⏳ Cargando datos...' : '📭 No hay datos disponibles con los filtros seleccionados'}
                </div>
              )}
              
              {/* Paginación */}
              {dataDetallada?.pagination && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <div className="text-sm text-gray-600">
                    Mostrando {((dataDetallada.pagination.page - 1) * dataDetallada.pagination.page_size) + 1} - {Math.min(dataDetallada.pagination.page * dataDetallada.pagination.page_size, dataDetallada.pagination.total_records)} de {dataDetallada.pagination.total_records} registros
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPaginaDetallada(p => Math.max(1, p - 1))}
                      disabled={!dataDetallada.pagination.has_prev || loadingData}
                      className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                    >
                      ← Anterior
                    </button>
                    <span className="text-sm text-gray-600">
                      Página {dataDetallada.pagination.page} de {dataDetallada.pagination.total_pages}
                    </span>
                    <button
                      onClick={() => setPaginaDetallada(p => p + 1)}
                      disabled={!dataDetallada.pagination.has_next || loadingData}
                      className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                    >
                      Siguiente →
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Tab: Top Facturas */}
          {activeResultTab === 'facturas' && results.top_facturas && (
            <div className="overflow-x-auto">
              <p className="text-sm text-gray-600 mb-3">Top {results.top_facturas.length} facturas que generan mayor pérdida de utilidad</p>
              <table className="w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-3 py-2 text-left">Factura</th>
                    <th className="px-3 py-2 text-left">PDV</th>
                    <th className="px-3 py-2 text-right">Venta</th>
                    <th className="px-3 py-2 text-right">Utilidad</th>
                    <th className="px-3 py-2 text-right text-red-600">Pérdida</th>
                    <th className="px-3 py-2 text-right">Margen %</th>
                    <th className="px-3 py-2 text-left">Driver</th>
                  </tr>
                </thead>
                <tbody>
                  {results.top_facturas.map((f, idx) => (
                    <tr key={idx} className="border-b hover:bg-gray-50">
                      <td className="px-3 py-2 font-mono text-xs">{f.IdFacturaCab}</td>
                      <td className="px-3 py-2 text-xs">{f.CODIGOPDV}</td>
                      <td className="px-3 py-2 text-right">${parseFloat(f['Venta Neta'] || 0).toFixed(2)}</td>
                      <td className="px-3 py-2 text-right">${parseFloat(f['Utilidad Bruta'] || 0).toFixed(2)}</td>
                      <td className="px-3 py-2 text-right text-red-600 font-bold">${parseFloat(f.Perdida_Linea || 0).toFixed(2)}</td>
                      <td className="px-3 py-2 text-right">{parseFloat(f.Margen_Factura || 0).toFixed(1)}%</td>
                      <td className="px-3 py-2 text-xs max-w-xs truncate">{f.Driver}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Tab: Top Productos */}
          {activeResultTab === 'productos' && results.top_productos && (
            <div className="overflow-x-auto">
              <p className="text-sm text-gray-600 mb-3">Top {results.top_productos.length} productos que destruyen utilidad</p>
              <table className="w-full text-sm" data-testid="tabla-top-productos">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-3 py-2 text-left">ID</th>
                    <th className="px-3 py-2 text-left">Producto</th>
                    <th className="px-3 py-2 text-right text-red-600">Pérdida</th>
                    <th className="px-3 py-2 text-right">Venta</th>
                    <th className="px-3 py-2 text-right">Margen %</th>
                    <th className="px-3 py-2 text-right">Facturas</th>
                    <th className="px-3 py-2 text-left">Driver</th>
                  </tr>
                </thead>
                <tbody>
                  {results.top_productos.map((p, idx) => (
                    <tr key={idx} className="border-b hover:bg-gray-50">
                      <td className="px-3 py-2 font-mono text-xs">{p.IdProducto}</td>
                      <td className="px-3 py-2 text-xs max-w-xs truncate">{p.Producto}</td>
                      <td className="px-3 py-2 text-right text-red-600 font-bold">${parseFloat(p.Perdida || 0).toFixed(2)}</td>
                      <td className="px-3 py-2 text-right">${parseFloat(p.Venta || 0).toFixed(2)}</td>
                      <td className="px-3 py-2 text-right">{parseFloat(p['Margen_%'] || 0).toFixed(1)}%</td>
                      <td className="px-3 py-2 text-right">{p.Facturas}</td>
                      <td className="px-3 py-2 text-xs max-w-xs truncate">{p.Driver}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Tab: Top Promociones */}
          {activeResultTab === 'promociones' && (
            <div className="overflow-x-auto">
              {results.top_promociones && results.top_promociones.length > 0 ? (
                <>
                  <p className="text-sm text-gray-600 mb-3">Top {results.top_promociones.length} promociones con destrucción de utilidad</p>
                  <table className="w-full text-sm" data-testid="tabla-top-promociones">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-3 py-2 text-left">ID Plan</th>
                        <th className="px-3 py-2 text-left">Descripción Promoción</th>
                        <th className="px-3 py-2 text-right text-red-600">Pérdida</th>
                        <th className="px-3 py-2 text-right">Venta</th>
                        <th className="px-3 py-2 text-right">Margen %</th>
                        <th className="px-3 py-2 text-right">Facturas</th>
                        <th className="px-3 py-2 text-right">Productos</th>
                        <th className="px-3 py-2 text-left">Driver</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.top_promociones.map((promo, idx) => (
                        <tr key={idx} className="border-b hover:bg-gray-50">
                          <td className="px-3 py-2 font-mono text-xs">{promo.IdPlan}</td>
                          <td className="px-3 py-2 text-xs max-w-xs truncate">{promo['Descripcion Plan'] || promo.DescripcionPlan || '-'}</td>
                          <td className="px-3 py-2 text-right text-red-600 font-bold">${parseFloat(promo.Perdida || 0).toFixed(2)}</td>
                          <td className="px-3 py-2 text-right">${parseFloat(promo.Venta || 0).toFixed(2)}</td>
                          <td className="px-3 py-2 text-right">{parseFloat(promo['Margen_%'] || 0).toFixed(1)}%</td>
                          <td className="px-3 py-2 text-right">{promo.Facturas}</td>
                          <td className="px-3 py-2 text-right">{promo.Productos}</td>
                          <td className="px-3 py-2 text-xs max-w-xs truncate">{promo.Driver}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>📭 No se encontraron promociones con pérdida de utilidad en los datos analizados.</p>
                  <p className="text-sm mt-2">Asegúrese de que el archivo contenga la columna "IdPlan" o "Descripcion Plan".</p>
                </div>
              )}
            </div>
          )}
          
          {/* Tab: Historial de Capturas */}
          {activeResultTab === 'historial' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h5 className="font-bold text-gray-800">📜 Historial de Análisis Capturados</h5>
                <button
                  onClick={fetchHistorialCapturas}
                  disabled={loadingHistorial}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200"
                >
                  {loadingHistorial ? '⏳ Cargando...' : '🔄 Actualizar'}
                </button>
              </div>
              
              {historialCapturas.length > 0 ? (
                <div className="space-y-4">
                  {historialCapturas.map((captura, idx) => (
                    <div key={idx} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex flex-wrap justify-between items-start gap-4">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-lg">📊</span>
                            <span className="font-bold text-gray-800">
                              {new Date(captura._fecha_captura).toLocaleDateString('es-ES', { 
                                year: 'numeric', month: 'long', day: 'numeric', 
                                hour: '2-digit', minute: '2-digit' 
                              })}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500">Archivo: {captura._archivo_origen}</p>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div className="text-center">
                            <p className="text-xs text-red-600">Pérdida</p>
                            <p className="font-bold text-red-700">${captura.resumen?.perdida_total?.toLocaleString() || 0}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-blue-600">Venta</p>
                            <p className="font-bold text-blue-700">${captura.resumen?.venta_total?.toLocaleString() || 0}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-gray-600">Facturas</p>
                            <p className="font-bold text-gray-700">{captura.resumen?.total_facturas?.toLocaleString() || 0}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-gray-600">Productos</p>
                            <p className="font-bold text-gray-700">{captura.resumen?.total_productos?.toLocaleString() || 0}</p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Drivers principales */}
                      {captura.top_causas && captura.top_causas.length > 0 && (
                        <div className="mt-3 pt-3 border-t">
                          <p className="text-xs text-gray-500 mb-2">Drivers principales:</p>
                          <div className="flex flex-wrap gap-2">
                            {captura.top_causas.slice(0, 4).map((causa, i) => (
                              <span key={i} className="px-2 py-1 bg-amber-100 text-amber-800 rounded text-xs">
                                {causa.causa}: {causa.ocurrencias}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Top productos */}
                      {captura.top_productos && captura.top_productos.length > 0 && (
                        <div className="mt-3 pt-3 border-t">
                          <p className="text-xs text-gray-500 mb-2">Top 3 productos con pérdida:</p>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                            {captura.top_productos.slice(0, 3).map((prod, i) => (
                              <div key={i} className="bg-red-50 rounded p-2 text-xs">
                                <p className="font-mono text-gray-600">{prod.IdProducto}</p>
                                <p className="text-red-600 font-bold">-${parseFloat(prod.Perdida || 0).toLocaleString()}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <span className="text-4xl">📭</span>
                  <p className="mt-2">No hay capturas guardadas</p>
                  <p className="text-sm">Use el botón "💾 Capturar Resultados" después de ejecutar un análisis</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}



const AppWithOverlay = () => (
  <ActionOverlayProvider>
    <App />
  </ActionOverlayProvider>
);

export default AppWithOverlay;

